import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageTitle } from "../hooks/usePageTitle";
import { ArrowLeft, AlertTriangle, ArrowUpToLine, Check, Info, MapPin, Package, PackageCheck, PackageOpen, PackageX, Tags, X } from "lucide-react";
import { toast } from "react-toastify";

import "../styles/scansheet.css";
import "../styles/book-in.css";
// For .picking-tier-toggle / .picking-tier-pill — the shared title-bar pill
// toggle, same as Shelf Drop's Pick/Put-Away switch.
import "../styles/picking.css";
// For the stock-check-* count rows the "Stock already here" flow renders —
// the same list the Stock Check module and Picking/Drop's missing-item
// counts use, so a count looks the same everywhere.
import "../styles/stock-check.css";
import MissingItemCountList, { type StockCheckLine } from "../components/picking/MissingItemCountList";
import { useTranslation } from "../i18n";
import { logoutProcessor } from "../components/helpers/logoutProcessor";
import { sameWarehouse } from "../components/helpers/warehouseAlias";
import {
    playSuccess,
    playError,
    playBoxAdd,
    playToteFinalised,
    playGreenHotSound,
    playGreenColdSound,
} from "../components/helpers/audioPlayer";
import HiddenScanInput from "../components/HiddenScanInput";
import { useDemoVisible } from "../components/helpers/demoMode";
import {
    preventScanPageButtonFocus,
    blurClickedScanPageButton,
} from "../components/helpers/scanFocusGuard";
import FitWord from "../components/FitWord";
import ScanStatusIcons from "../components/ScanStatusIcons";
import ScanFlashOverlay from "../components/ScanFlashOverlay";
import FlowLoader from "../components/FlowLoader";
import ScannedItemCard from "../components/put-away-shared/ScannedItemCard";
import LastPutAwayBanner, { type LastPutAwayItem } from "../components/put-away-shared/LastPutAwayBanner";
import { CONFIRM_ARM_MS } from "../components/put-away-shared/confirmArming";
import WrongQueueModal from "../components/put-away-shared/WrongQueueModal";
import { lookupWrongQueue, type WrongQueueHome } from "../components/put-away-shared/toteWhereabouts";
import { useProgressTimer, ProgressBar, useSessionScore, ScoreDisplay } from "../components/gamification";
import { apiFetch } from "../api/apiFetch";
// The BF scan sheet's item-scan verdict faces — the category card reuses
// them so hot/cold looks the same everywhere.
import greenHotImg from "../images/green-hot.png";
import greenColdImg from "../images/green-cold.png";

const DEFAULT_DURATION_MS = 6000;
const KEYBOARD_BONUS_MS = 20000;
const ERROR_PAUSE_MS = 2000;
const SESSION_KEY = "put-away-in-progress";

const TOAST_ID = {
    nothingScanned: "pa-nothing-scanned",
    notATote: "pa-not-a-tote",
    invalidLocation: "pa-invalid-location",
    unknownScan: "pa-unknown-scan",
    serverError: "pa-server-error",
    toteClosed: "pa-tote-closed",
    ppdFailed: "pa-ppd-failed",
    confirmMismatch: "pa-confirm-mismatch",
    highNotHigh: "pa-high-not-high",
    highWrongLocation: "pa-high-wrong-location",
    highMissingDone: "pa-high-missing-done",
    coldTote: "pa-cold-tote",
    coldOccupiedRace: "pa-cold-occupied-race",
    coldNone: "pa-cold-none",
    consTote: "pa-cons-tote",
    consNone: "pa-cons-none",
    consScan: "pa-cons-scan",
    consSkipped: "pa-cons-skipped",
    consTakeTote: "pa-cons-take-tote",
    consEnded: "pa-cons-ended",
    consResumed: "pa-cons-resumed",
    consLocationEmpty: "pa-cons-location-empty",
    consClosed: "pa-cons-closed",
    consBookedIn: "pa-cons-booked-in",
    consWrittenOff: "pa-cons-written-off",
} as const;

// Phases:
//   awaiting-tote         → scan the tote to claim it
//   awaiting-location     → in-hand: next scan is a location (full-tote) or,
//                            if item-level is enabled, an item barcode from
//                            the tote (switches to item-level flow).
//   awaiting-item-location → item-level: picked an item, need its location.
type Phase = "awaiting-tote" | "awaiting-location" | "awaiting-item-location";

// Top-of-page mode. "put-away" is the default and the only mode that moves
// stock; "category" is a read-only lookup (scan a tote → see its category →
// scan the next), so it never claims a tote or writes any state.
type Mode = "put-away" | "category" | "high-empty";

// Empty High Locations mode — a read-only navigator. The operator scans the
// high location they are standing at; the BE answers with the nearest EMPTY
// high location forward in walk order (wrap-around, the same rule Picking
// uses). Scanning that location on arrival makes it the new anchor and the
// next one is served. Nothing is claimed or moved.
type HighLocation = {
    barcode: string;
    // Picker-hero convention (aisle-bay-level, e.g. N-4-L12), falling back
    // to display_name then the raw barcode — resolved by the BE. Barcode
    // stays raw: arrival scans are compared against it.
    display: string;
    walkOrder: number | null;
};
type HighEmptyState = {
    anchor: HighLocation;
    next: HighLocation | null;
};

// The Empty High Locations pill has two views at Pembroke (per Alex,
// 2026-09-22): "empty" is the read-only navigator above; "consolidate" is
// the guided walk below.
type HighView = "empty" | "consolidate";

// Consolidate — gather the 1–2-unit high locations onto one tote so the
// racking is freed. A guided Move Stock: the BE tote/session/moves are the
// Move Stock engine's (routes under /put-away/consolidate/*), so the tote
// ends up in the ordinary Put Away queue. Steps, one on screen at a time:
//   tote     → scan the tote to consolidate into (resumes an open one)
//   anchor   → scan where you are; BE answers with the nearest candidate
//   location → walk there; scan it on arrival. Tapping the pin = "Change
//              location" (Picking's gesture): scan the location you're
//              moving to; nothing is recorded.
//   items    → scan each unit onto the tote. The card lists what the data
//              says is here (FNSKU + title, per Alex 2026-09-23) — every
//              unit must still be scanned; a row's "Not here" writes the
//              unit(s) the shelf doesn't actually hold off.
// "Tote full" (per Alex, 2026-09-22) is the bulky-items button: the units
// here aren't worth moving, so the location stays off everyone's list until
// something is picked from it.
// Totes open and close exactly as in Shelf Drop (per Alex, 2026-09-22):
// rescanning the tote in hand opens a Close Tote confirm (rescan again or
// tap to confirm; ends the walk), scanning a FRESH tote mid-walk opens a
// Change Tote confirm (the current tote goes to put-away, the walk carries
// on into the new one), and a closed tote scanned again offers Reopen.
type ConsolidateStep = "tote" | "anchor" | "location" | "items";
type ConsolidateContent = {
    sku: string;
    title: string;
    barcode: string | null;
    thumbnailUrl: string | null;
    qty: number;
    /** Units present when the suggestion / arrival loaded — the row's total. */
    startQty: number;
};
type ConsolidateTarget = HighLocation & {
    units: number | null;
    contents: ConsolidateContent[];
};
type ConsolidateState = {
    step: ConsolidateStep;
    sessionId: number | null;
    toteId: string | null;
    movedUnits: number;
    anchor: HighLocation | null;
    target: ConsolidateTarget | null;
};
const CONS_INITIAL: ConsolidateState = {
    step: "tote", sessionId: null, toteId: null, movedUnits: 0, anchor: null, target: null,
};
const shapeConsolidateContent = (c: {
    sku?: unknown; title?: unknown; barcode?: unknown; thumbnailUrl?: unknown; qty?: unknown;
}): ConsolidateContent => {
    const qty = typeof c.qty === "number" ? c.qty : Number(c.qty ?? 0) || 0;
    return {
        sku: String(c.sku ?? ""),
        title: typeof c.title === "string" && c.title ? c.title : String(c.sku ?? ""),
        barcode: typeof c.barcode === "string" && c.barcode ? c.barcode : null,
        thumbnailUrl: typeof c.thumbnailUrl === "string" && c.thumbnailUrl ? c.thumbnailUrl : null,
        qty,
        startQty: qty,
    };
};
const shapeConsolidateTarget = (l: {
    barcode?: unknown; display?: unknown; walkOrder?: unknown; units?: unknown; contents?: unknown;
}): ConsolidateTarget => ({
    ...shapeHighLocation(l),
    units: typeof l.units === "number" ? l.units : null,
    contents: Array.isArray(l.contents) ? l.contents.map(shapeConsolidateContent) : [],
});

// Shared shape for a "next empty location" answer — Empty High Locations
// and Cold Putaway both get this back from the BE (Cold reuses the exact
// same /put-away/high-empty/* routes, scoped to Pembroke's cold racking by
// the same walk-order/zone logic those routes already apply). barcode
// stays raw for arrival-scan comparison; display is picker-hero formatted.
const shapeHighLocation = (l: { barcode?: unknown; display?: unknown; walkOrder?: unknown }): HighLocation => {
    const barcode = String(l.barcode ?? "");
    return {
        barcode,
        display: typeof l.display === "string" && l.display ? l.display : barcode,
        walkOrder: typeof l.walkOrder === "number" ? l.walkOrder : null,
    };
};

// Cold Putaway (Pembroke only) — layered inside Put Away mode's
// awaiting-tote phase, not a separate top-level mode:
//   anchor   → scan where you're standing; BE answers with the nearest
//              empty cold location forward in walk order.
//   tote     → walk there, check it's empty, then scan a closed Cold tote.
//   location → scan the suggested location itself to commit the move.
// Hot is not built yet and (per Alex, 2026-09-21) not shown at all until
// it is — the entry point is the BF scan sheet's cold face alone.
type ColdStep = "anchor" | "tote" | "location";

// "Stock already here" remediation (per Alex, 2026-09-21). Offered from
// the Cold Putaway card and, since 2026-09-23 (per Alex), from the Empty
// High Locations card too — the data says the shelf is empty, the eyes say
// otherwise, and the fix is the same either way. Forced and in order —
// only the current step is on screen (the MissingLocationGate lesson:
// render the next step and operators read it as already started):
//   location → scan the suggested location to prove they're stood at it
//   tote     → scan the tote that's physically there; the BE says whether
//              it is still awaiting put-away
//   release  → it is: one button commits THAT tote into this location
//   counting → it isn't: a real stock check of the location
//              (/put-away/found-stock/*), every scanned unit the data
//              lacks here is booked in on the spot via /stock-check/add
//              (per Alex, 2026-09-23: no "book it in?" prompt — if they
//              scanned it, it's here), Finish completes the check
//   destination → (per Alex, 2026-09-23) if anything was counted, Finish
//              goes straight to "scan the location to put away into" —
//              the Put Away model: tote → items → location. Most reports
//              are one unit left behind in an otherwise empty location,
//              and leaving it there just re-creates the problem. No
//              confirm, no "keep here" button; scanning THIS location is
//              the only way to book them in here. Moved out → the shelf
//              is empty again and the suggestion stays put; put away here
//              → excluded and the walk moves on. With nothing counted,
//              Finish asks for a confirm and completes the check.
// Every terminal branch ends the way the old advice popup did: exclude the
// location for this session and ask for the next one (Cold refreshes its
// suggestion; Empty High Locations treats it as an arrival and walks on
// from there). Cancel exists only
// before anything is written (location / tote / release) and undoes the
// report WITHOUT excluding — they're still being sent here.
type FoundStage = "location" | "tote" | "release" | "counting" | "destination";
type FoundState = {
    location: HighLocation;
    stage: FoundStage;
    /** The tote found in the location, once scanned. */
    toteId: string | null;
    /** Backing stock check (counting stage). */
    checkId: number | null;
    lines: StockCheckLine[];
    /** Units booked in through /stock-check/add this visit. */
    added: number;
};

// A tote's category comes from whichever flow last booked it in, and the two
// flows use DIFFERENT vocabularies that overlap on "RED". `source` is what
// tells them apart, so the swatch is always keyed on source + category —
// never on category alone.
//   book_in    → Goods-In tote colours
//   bf_scan    → BargainFox scan-sheet colours
//   move_stock → never sets a category (always null)
type CategorySource = "book_in" | "bf_scan" | "move_stock";
type CategoryResult = {
    toteId: string;
    category: string | null;
    source: CategorySource;
    // Supplier book-in totes carry no colour but do carry the PO they were
    // booked against; the card shows "{supplier} · {po}" for them.
    supplierName: string | null;
    poNumber: string | null;
    unitsTotal: number;
};

// Swatch + label per vocabulary. Goods-In values mirror book-in.tsx's
// COLOUR_CATEGORIES; BF values mirror BfScanSheet.tsx's BF_COLOURS.
const CATEGORY_STYLES: Record<string, { swatch: string; text: string; labelKey: string }> = {
    // Goods-In (source 'book_in')
    "book_in:BLUE":     { swatch: "#2563eb", text: "#ffffff", labelKey: "colour_blue" },
    "book_in:GREEN":    { swatch: "#16a34a", text: "#ffffff", labelKey: "colour_green" },
    "book_in:GREY":     { swatch: "#6b7280", text: "#ffffff", labelKey: "colour_grey" },
    "book_in:ORANGE":   { swatch: "#f97316", text: "#ffffff", labelKey: "colour_orange" },
    "book_in:PINK":     { swatch: "#ec4899", text: "#ffffff", labelKey: "colour_pink" },
    "book_in:RED":      { swatch: "#ef4444", text: "#ffffff", labelKey: "colour_red" },
    "book_in:YELLOW":   { swatch: "#eab308", text: "#ffffff", labelKey: "colour_yellow" },
    "book_in:MISC":     { swatch: "#0f172a", text: "#ffffff", labelKey: "category_misc" },
    "book_in:SUPPLIER": { swatch: "#0f172a", text: "#ffffff", labelKey: "category_supplier" },
    // BargainFox scan sheet (source 'bf_scan')
    "bf_scan:HOT":      { swatch: "#16a34a", text: "#ffffff", labelKey: "bf_colour_hot" },
    "bf_scan:COLD":     { swatch: "#0ea5e9", text: "#ffffff", labelKey: "bf_colour_cold" },
    "bf_scan:RED":      { swatch: "#FF1617", text: "#ffffff", labelKey: "bf_colour_red" },
    "bf_scan:MEASURE":  { swatch: "#a855f7", text: "#ffffff", labelKey: "bf_colour_measure" },
};

const NO_CATEGORY_SWATCH = { swatch: "#e7e7ea", text: "#3f3f46" };

// Resolve a result to its swatch/label. Keyed on source+category first,
// because "RED" is a legal value in BOTH vocabularies and only source can
// tell those two apart.
//
// Then fall back to the other vocabulary: legacy rows booked in before
// migration 136 added book_ins.source have source NULL, which the BE
// coalesces to 'book_in' — so real BF scan-sheet book-ins from that era
// surface as book_in:COLD / book_in:HOT / book_in:MEASURE. Dev data has
// 17 such rows. Without this fallback they'd render on the grey
// "no swatch" panel despite having a perfectly good colour.
//
// Shared by the render (swatch + face image) and the scan handler (which
// sound to play), so hot/cold can never look one thing and sound another.
const resolveCategoryStyle = (result: CategoryResult | null) => {
    const value = result?.category;
    if (!value) return null;
    const exact = CATEGORY_STYLES[`${result!.source}:${value}`];
    if (exact) return exact;
    const other = result!.source === "bf_scan" ? "book_in" : "bf_scan";
    return CATEGORY_STYLES[`${other}:${value}`] ?? null;
};
type ItemMeta = {
    sku: string | null;
    title: string | null;
    thumbnailUrl: string | null;
    imageUrl: string | null;
};

const PutAway = () => {
    usePageTitle("put_away");
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { t } = useTranslation();
    const demo = useDemoVisible();

    const userData = window.localStorage.getItem("user");
    const userObject =
        userData !== null ? JSON.parse(userData) : { uid: 0, name: "", permissions: "", warehouse: "Unknown" };
    // Cold Putaway is hard-gated to Pembroke for now (per Gabe, 2026-09-18) —
    // canonical name, so the legacy "Pembroke Dock" alias still matches.
    const isPembroke = sameWarehouse(userObject.warehouse, "Pembroke");

    // Always start at awaiting-tote — the first action on entering the page
    // must be scanning a tote. Any in-progress put-away from a previous
    // navigation is discarded so operators always have a clear starting point.
    const [phase, setPhase] = useState<Phase>("awaiting-tote");
    const [toteId, setToteId] = useState<string | null>(null);
    // totes.type of the claimed tote ('box' | 'pallet' | 'york'). A pallet
    // crate's barcode is ALSO its storage location (Alex, 2026-10-12), so
    // the rescan-closes rule is swapped for "put away into the crate" and a
    // Close button takes over the abandon gesture.
    const [toteType, setToteType] = useState<string | null>(null);
    const [remaining, setRemaining] = useState<string[]>([]);
    const [pickedItem, setPickedItem] = useState<string | null>(null);
    const [pickedItemMeta, setPickedItemMeta] = useState<LastPutAwayItem | null>(null);
    const [lastPutAway, setLastPutAway] = useState<LastPutAwayItem | null>(null);
    const [allowItemLevel, setAllowItemLevel] = useState(false);
    const [scanValue, setScanValue] = useState("");
    const [loading, setLoading] = useState(false);
    // True while auto-claiming a tote handed off from Book In's "Complete &
    // Putaway". During the brief claim round-trip we suppress the scan-tote
    // command and the success flash so the operator lands straight on the
    // scan item/location step with no intermediate screens. Initialised from
    // the navigation state so even the first frame skips the tote-scan UI.
    const [handoffClaiming, setHandoffClaiming] = useState<boolean>(
        () => !!(routerLocation.state as { autoClaimTote?: string } | null)?.autoClaimTote,
    );
    const [scanStatus, setScanStatus] = useState<"idle" | "success" | "error" | "final">("idle");
    // Put Away is the default mode; the category lookup is opt-in per scan
    // session and resets to the default on every page entry.
    const [mode, setMode] = useState<Mode>("put-away");
    const [categoryResult, setCategoryResult] = useState<CategoryResult | null>(null);
    // Bumped on every successful category lookup and used as the result's
    // React key: scanning a tote the SAME colour as the last one remounts
    // the card, replaying its flash-to-solid animation — without this a
    // repeat colour looks like the scan did nothing.
    const [categoryScanSeq, setCategoryScanSeq] = useState(0);

    // Empty High Locations mode. null = awaiting the anchor scan. The ref
    // mirrors state for the scan handler (doSubmit can run from a closure
    // the scanner captured earlier). Visited = every suggested location the
    // operator has confirmed arriving at this session: nothing is moved in
    // this mode, so the data still says they are empty and they would be
    // suggested again (distance 0 from themselves) without this exclusion.
    // Across sessions and people the BE remembers instead (per Alex,
    // 2026-10-14, after operators were walked to shelves the previous
    // person had already brought down): an arrival scan is sent as
    // `arrivedAt`, which stamps the location cleared, and every lookup in
    // this mode sends `skipCleared` so stamped locations are left out until
    // stock lands there again and is picked back down. Cold Putaway shares
    // the lookup but sends neither — a cleared, empty slot is exactly what
    // it wants to fill.
    const [highEmpty, setHighEmpty] = useState<HighEmptyState | null>(null);
    const highEmptyRef = useRef<HighEmptyState | null>(null);
    const highVisitedRef = useRef<string[]>([]);
    const [highEmptySeq, setHighEmptySeq] = useState(0);
    // "Location doesn't exist" confirm: the suggestion on screen when the
    // operator tapped the button. Ref-mirrored so a scan arriving while the
    // prompt is open can be ignored rather than advancing the card behind it.
    const [highMissingConfirm, setHighMissingConfirm] = useState<HighLocation | null>(null);
    const highMissingRef = useRef<HighLocation | null>(null);
    const [confirmingHighMissing, setConfirmingHighMissing] = useState(false);

    // Consolidate (Pembroke only) — see ConsolidateState. Refs mirror state
    // for the scan handler, written FIRST in every transition (a scan can
    // land before React commits). consDoneRef is this session's list of
    // locations emptied or skipped, passed back as `exclude` like Empty
    // High Locations' visited list — an emptied location is no longer a
    // candidate anyway, but a half-moved or skipped one still is until the
    // BE's skip memory (or the operator's next visit) says otherwise.
    const [highView, setHighView] = useState<HighView>("empty");
    const highViewRef = useRef<HighView>("empty");
    const setHighViewState = (v: HighView) => { highViewRef.current = v; setHighView(v); };
    const [cons, setCons] = useState<ConsolidateState>(CONS_INITIAL);
    const consRef = useRef<ConsolidateState>(CONS_INITIAL);
    const setConsState = (next: ConsolidateState | ((prev: ConsolidateState) => ConsolidateState)) => {
        const v = typeof next === "function" ? next(consRef.current) : next;
        consRef.current = v;
        setCons(v);
    };
    const consDoneRef = useRef<string[]>([]);
    const [consChangeOpen, setConsChangeOpen] = useState(false);
    const consChangeOpenRef = useRef(false);
    const setConsChangeOpenState = (open: boolean) => { consChangeOpenRef.current = open; setConsChangeOpen(open); };
    // "Reopen tote?" — a tote this screen closed earlier, scanned again;
    // `mode` says whether it was scanned to start with (open it) or
    // mid-walk (change to it).
    type ConsReopen = { tote: string; units: number; mode: "start" | "swap" };
    const [consReopen, setConsReopen] = useState<ConsReopen | null>(null);
    const consReopenRef = useRef<ConsReopen | null>(null);
    const setConsReopenState = (v: ConsReopen | null) => { consReopenRef.current = v; setConsReopen(v); };
    const [consFullOpen, setConsFullOpen] = useState(false);
    const consFullOpenRef = useRef(false);
    const setConsFullOpenState = (open: boolean) => { consFullOpenRef.current = open; setConsFullOpen(open); };
    // Close Tote confirm (Shelf Drop): the first tote rescan only opens
    // this; the same tote rescanned, or the button, closes.
    type ConsClose = { toteId: string; units: number };
    const [consClose, setConsClose] = useState<ConsClose | null>(null);
    const consCloseRef = useRef<ConsClose | null>(null);
    const setConsCloseState = (v: ConsClose | null) => { consCloseRef.current = v; setConsClose(v); };
    // Change Tote confirm (Shelf Drop): a fresh tote scanned mid-walk, checked
    // by the BE (dry run) before the prompt; rescanning it or Confirm swaps.
    type ConsSwap = { toteId: string };
    const [consSwap, setConsSwap] = useState<ConsSwap | null>(null);
    const consSwapRef = useRef<ConsSwap | null>(null);
    const setConsSwapState = (v: ConsSwap | null) => { consSwapRef.current = v; setConsSwap(v); };
    // "Book it in and move it?" — a known product scanned at a location the
    // data doesn't have it at (per Alex, 2026-09-22). Confirming books one
    // unit in here and moves it onto the tote in one go.
    type ConsBookIn = { sku: string; title: string; barcode: string };
    const [consBookIn, setConsBookIn] = useState<ConsBookIn | null>(null);
    const consBookInRef = useRef<ConsBookIn | null>(null);
    const setConsBookInState = (v: ConsBookIn | null) => { consBookInRef.current = v; setConsBookIn(v); };
    // "Not here" → write off confirm for one row of the items list.
    const [consWriteOff, setConsWriteOff] = useState<ConsolidateContent | null>(null);
    const consWriteOffRef = useRef<ConsolidateContent | null>(null);
    const setConsWriteOffState = (v: ConsolidateContent | null) => { consWriteOffRef.current = v; setConsWriteOff(v); };
    const [consBusy, setConsBusy] = useState(false);
    const [consSeq, setConsSeq] = useState(0);

    // Cold Putaway. null = not active. Refs mirror state for the scan
    // handler, same reasoning as highEmptyRef/highVisitedRef above.
    // coldExcludeRef plays the same role as highVisitedRef: nothing is
    // moved by the anchor/report steps, so the data still says those
    // locations are empty and they'd be suggested again without it.
    const [coldStep, setColdStep] = useState<ColdStep | null>(null);
    const coldStepRef = useRef<ColdStep | null>(null);
    const [coldAnchor, setColdAnchor] = useState<HighLocation | null>(null);
    const [coldSuggested, setColdSuggested] = useState<HighLocation | null>(null);
    const coldSuggestedRef = useRef<HighLocation | null>(null);
    const coldExcludeRef = useRef<string[]>([]);
    // "Location doesn't exist" — identical mechanism to Empty High
    // Locations' own confirm (marks the location unsellable for good).
    const [coldMissingConfirm, setColdMissingConfirm] = useState<HighLocation | null>(null);
    const [confirmingColdMissing, setConfirmingColdMissing] = useState(false);
    // "Stock already here" — see FoundState. Refs are written FIRST in every
    // transition (setFoundState), because a scan can arrive before React
    // commits the render and must already be intercepted by the flow —
    // same rule Drop's missing flow follows.
    const [found, setFound] = useState<FoundState | null>(null);
    const foundRef = useRef<FoundState | null>(null);
    const [foundFinishConfirm, setFoundFinishConfirm] = useState(false);
    const foundFinishConfirmRef = useRef(false);
    const [foundBusy, setFoundBusy] = useState(false);
    const setFoundState = (next: FoundState | null) => {
        foundRef.current = next;
        setFound(next);
    };
    const setFoundFinishConfirmState = (open: boolean) => {
        foundFinishConfirmRef.current = open;
        setFoundFinishConfirm(open);
    };
    useEffect(() => { coldStepRef.current = coldStep; }, [coldStep]);
    useEffect(() => { coldSuggestedRef.current = coldSuggested; }, [coldSuggested]);

    // ⓘ popup: empty high locations per zone, in walk order (same shape as
    // Shelf Drop's units-by-aisle popup). Fetched on open, never polled.
    // null = loading; [] = fetch failed / nothing to show.
    type HighZoneRow = { label: string; empty: number };
    const [highZonesOpen, setHighZonesOpen] = useState(false);
    // The popup fetch failed (server error / route missing) — shown as a
    // failure, never as "nothing to show" (per Alex, 2026-09-22: a missing
    // table read as an empty list).
    const [highZonesFailed, setHighZonesFailed] = useState(false);
    // Wrong-queue prompt: a rejected tote that belongs to Shelf/Forecast
    // Drop put-away (or a picking wave) — names its home, offers to go there.
    const [wrongQueue, setWrongQueue] = useState<(WrongQueueHome & { toteId: string }) | null>(null);
    const [highZoneRows, setHighZoneRows] = useState<HighZoneRow[] | null>(null);
    const openHighZones = async () => {
        setHighZonesOpen(true);
        setHighZoneRows(null);
        setHighZonesFailed(false);
        try {
            const params = new URLSearchParams();
            if (userObject.warehouse) params.set("warehouse", userObject.warehouse);
            // Same popup, other count: in the Consolidate view the rows are
            // locations with 1–2 units rather than empty ones. (uid rides
            // along for the dev-bypass identity path, as the other
            // Consolidate calls do.)
            const consolidate = highViewRef.current === "consolidate";
            if (consolidate && userObject.uid) params.set("uid", String(userObject.uid));
            const res = await apiFetch(consolidate
                ? `/put-away/consolidate/zones?${params.toString()}`
                : `/put-away/high-empty/aisles?${params.toString()}`);
            if (!res.ok) {
                setHighZonesFailed(true);
                setHighZoneRows([]);
                return;
            }
            const d = await res.json();
            const rows = Array.isArray(d?.rows) ? d.rows : [];
            setHighZoneRows(consolidate
                ? rows.map((r: { label: string; count?: number; empty?: number }) => ({
                    label: r.label, empty: Number(r.count ?? r.empty ?? 0),
                }))
                : rows);
        } catch {
            setHighZonesFailed(true);
            setHighZoneRows([]);
        }
    };
    const modeRef = useRef(mode);
    useEffect(() => { modeRef.current = mode; }, [mode]);
    // Demo buttons — real locations fetched from the DB, scoped to the
    // operator's warehouse, so clicking a demo button always hits a valid
    // barcode regardless of which locations admin has seeded.
    const [demoLocations, setDemoLocations] = useState<string[]>([]);

    // Item-level unsellable locations retain their existing warning prompt.
    // Full-tote moves use toteConfirm for both target sellability states.
    // displayName is the location's human-readable name — shown in the
    // prompts instead of the scanned barcode; locationId stays the raw
    // barcode because scan-to-confirm compares against it.
    type UnsellableConfirm = { mode: "item"; locationId: string; displayName?: string; itemBarcode: string };
    const [unsellableConfirm, setUnsellableConfirm] = useState<UnsellableConfirm | null>(null);
    type ToteConfirm = {
        locationId: string;
        displayName?: string;
        sellable: "sellable" | "unsellable";
        units: number;
    };
    const [toteConfirm, setToteConfirm] = useState<ToteConfirm | null>(null);
    const toteConfirmRef = useRef<ToteConfirm | null>(null);
    // When the confirm prompt opened — a matching location scan inside
    // CONFIRM_ARM_MS of this is the echo of the scan that opened it
    // (double-fire / held-scan replay), not the operator's confirmation.
    const toteConfirmOpenedAtRef = useRef(0);
    const [confirmingTote, setConfirmingTote] = useState(false);
    const confirmingToteRef = useRef(false);

    // Refs for async closures
    const phaseRef = useRef(phase);
    const toteIdRef = useRef(toteId);
    const toteTypeRef = useRef<string | null>(null);
    const remainingRef = useRef(remaining);
    const pickedItemRef = useRef(pickedItem);
    const pickedItemMetaRef = useRef(pickedItemMeta);
    // barcode -> display metadata for the current tote's items, from claimTote.
    // Lets the local fast-path resolve product details without a round-trip when
    // the scanned item is already known to be in the tote.
    const itemMetaByBarcodeRef = useRef<Record<string, ItemMeta>>({});
    const allowItemLevelRef = useRef(allowItemLevel);
    useEffect(() => { phaseRef.current = phase; }, [phase]);
    useEffect(() => { toteIdRef.current = toteId; }, [toteId]);
    useEffect(() => { remainingRef.current = remaining; }, [remaining]);
    useEffect(() => { pickedItemRef.current = pickedItem; }, [pickedItem]);
    useEffect(() => { pickedItemMetaRef.current = pickedItemMeta; }, [pickedItemMeta]);
    useEffect(() => { allowItemLevelRef.current = allowItemLevel; }, [allowItemLevel]);
    useEffect(() => { toteConfirmRef.current = toteConfirm; }, [toteConfirm]);

    const selectPickedItem = (barcode: string, meta: ItemMeta | null) => {
        const displayItem: LastPutAwayItem = {
            barcode,
            sku: meta?.sku ?? barcode,
            title: meta?.title ?? null,
            thumbnailUrl: meta?.thumbnailUrl ?? null,
            imageUrl: meta?.imageUrl ?? null,
        };
        setPickedItem(barcode);
        pickedItemRef.current = barcode;
        setPickedItemMeta(displayItem);
        pickedItemMetaRef.current = displayItem;
    };

    const clearPickedItem = () => {
        setPickedItem(null);
        pickedItemRef.current = null;
        setPickedItemMeta(null);
        pickedItemMetaRef.current = null;
    };

    // Resolved progress-bar target (ms). Held in a ref so the latest value is
    // used by callbacks without retriggering renders. Falls back to the
    // hardcoded default until warehouse settings load.
    const defaultDurationRef = useRef(DEFAULT_DURATION_MS);

    // Fetch warehouse setting on mount — decides whether item-level scans
    // are accepted. Default false until resolved (safer — operators in
    // warehouses without item-level turned on can't accidentally trigger it).
    useEffect(() => {
        if (!userObject.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(
                    `/settings/${encodeURIComponent(userObject.warehouse)}`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                if (!alive) return;
                if (typeof data.allow_item_level_put_away === "boolean") {
                    setAllowItemLevel(data.allow_item_level_put_away);
                }
                if (typeof data.put_away_timer_ms === "number" && data.put_away_timer_ms > 0) {
                    defaultDurationRef.current = data.put_away_timer_ms;
                }
            } catch {
                // stay at default
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    // Fetch real locations for the demo panel, scoped to the operator's
    // warehouse. Falls back to empty — demo simply hides if there are none.
    useEffect(() => {
        if (!userObject.warehouse) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/put-away/locations`, {});
                if (!res.ok) return;
                const data: Array<{ barcode: string; warehouse: string }> = await res.json();
                if (!alive) return;
                const barcodes = data
                    .filter(l => l.warehouse === userObject.warehouse)
                    .map(l => l.barcode)
                    .slice(0, 4);
                setDemoLocations(barcodes);
            } catch {
                // leave demo buttons hidden
            }
        })();
        return () => { alive = false; };
    }, [userObject.warehouse]);

    // Clear any lingering session state from a previous visit. Put Away
    // always starts fresh — first action must be scanning a tote.
    useEffect(() => {
        window.sessionStorage.removeItem(SESSION_KEY);
    }, []);

    const timer = useProgressTimer();
    const score = useSessionScore();
    const keyboardOpenRef = useRef(false);

    const computeNextDurationMs = () =>
        defaultDurationRef.current + (keyboardOpenRef.current ? KEYBOARD_BONUS_MS : 0);

    // Abort in-flight fetches on unmount
    const abortRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => () => {
        mountedRef.current = false;
        abortRef.current?.abort();
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    }, []);

    const flashStatus = (status: "success" | "error" | "final") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        setScanStatus(status);
        // Final status lingers a touch longer so the yellow tick + finalised
        // sound land together visually.
        const ms = status === "final" ? 1200 : 800;
        statusTimerRef.current = setTimeout(() => setScanStatus("idle"), ms);
    };

    const getSignal = () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        return ctrl.signal;
    };

    const stopTimerAndAward = () => {
        if (!timer.state.active) return;
        const { elapsedMs, completed, durationMs } = timer.stop();
        if (!completed && durationMs > 0) {
            const secondsLeft = Math.max(0, Math.ceil((durationMs - elapsedMs) / 1000));
            score.addSeconds(secondsLeft, "put_away");
        }
    };

    const claimTote = async (
        val: string,
        expectedCategory?: string,
    ): Promise<{ valid: boolean; reason?: string; remaining?: string[]; meta?: Record<string, ItemMeta>; type?: string }> => {
        const res = await apiFetch(`/put-away/tote/claim`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: val,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
                ...(expectedCategory ? { expectedCategory } : {}),
            }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { valid: false }; }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    // Read-only category lookup for the "Tote Category" mode. Claims nothing
    // and moves nothing — the operator is only asking "what is this tote?".
    type CategoryLookup =
        | { valid: true; result: CategoryResult }
        | { valid: false; reason?: string };
    const lookupToteCategory = async (val: string): Promise<CategoryLookup> => {
        const res = await apiFetch(`/put-away/tote/category`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ toteId: val }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { valid: false }; }
        if (!res.ok) return { valid: false };
        const data = await res.json();
        if (!data?.valid) return { valid: false, reason: data?.reason };
        return {
            valid: true,
            result: {
                toteId: data.toteId ?? val,
                category: data.category ?? null,
                source: (data.source ?? "book_in") as CategorySource,
                supplierName: typeof data.supplierName === "string" ? data.supplierName : null,
                poNumber: typeof data.poNumber === "string" ? data.poNumber : null,
                unitsTotal: typeof data.unitsTotal === "number" ? data.unitsTotal : 0,
            },
        };
    };

    // Lookup for Empty High Locations mode: validates that the scanned
    // location is high and returns the nearest empty high location forward
    // in walk order from it, skipping `exclude` (this session's visited
    // list). `skipCleared` also leaves out locations any operator has
    // already cleared; `arrivedAt` stamps that location cleared first (the
    // navigator's arrival scan). See the highVisitedRef comment.
    type HighEmptyLookup =
        | { valid: true; result: HighEmptyState }
        | { valid: false; reason?: string };
    type HighEmptyLookupOpts = { skipCleared?: boolean; arrivedAt?: string };
    const lookupNextEmptyHigh = async (
        val: string,
        exclude: string[],
        opts: HighEmptyLookupOpts = {},
    ): Promise<HighEmptyLookup> => {
        const res = await apiFetch(`/put-away/high-empty/next`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locationId: val, exclude, ...opts }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { valid: false }; }
        if (!res.ok) return { valid: false };
        const data = await res.json();
        if (!data?.valid || !data.anchor) return { valid: false, reason: data?.reason };
        return {
            valid: true,
            result: {
                anchor: shapeHighLocation(data.anchor),
                next: data.next ? shapeHighLocation(data.next) : null,
            },
        };
    };

    // Asks the backend whether a scanned barcode is a pickable item for
    // this tote — returns "tote" if it's in the booked-in list, "scansheet"
    // if it's a known scansheet item and the warehouse allows out-of-tote
    // picks, or null for unknown.
    // Also returns the item's SKU and product title when the backend can
    // resolve them (null otherwise) so the location prompt can show them
    // under the barcode.
    type ItemCheck = ItemMeta & { source: "tote" | "scansheet" | null; reason?: string };
    const checkItem = async (val: string): Promise<ItemCheck> => {
        if (!toteIdRef.current) return {
            source: null, sku: null, title: null, thumbnailUrl: null, imageUrl: null,
        };
        const res = await apiFetch(`/put-away/item/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: toteIdRef.current,
                barcode: val,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) {
            logoutProcessor();
            navigate("/login");
            return { source: null, sku: null, title: null, thumbnailUrl: null, imageUrl: null };
        }
        if (!res.ok) return {
            source: null, sku: null, title: null, thumbnailUrl: null, imageUrl: null,
        };
        const data = await res.json();
        const source = (data?.valid && data?.source) ? data.source : null;
        return {
            source,
            reason: typeof data?.reason === "string" ? data.reason : undefined,
            sku: source && typeof data?.sku === "string" ? data.sku : null,
            title: source && typeof data?.title === "string" ? data.title : null,
            thumbnailUrl: source && typeof data?.thumbnailUrl === "string" ? data.thumbnailUrl : null,
            imageUrl: source && typeof data?.imageUrl === "string" ? data.imageUrl : null,
        };
    };

    type LocationCheck =
        | { valid: false }
        | { valid: true; sellable: "sellable" | "unsellable"; displayName?: string };
    const checkLocation = async (val: string): Promise<LocationCheck> => {
        const res = await apiFetch(`/put-away/location/check`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ locationId: val }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { valid: false }; }
        if (!res.ok) return { valid: false };
        const data = await res.json();
        if (!data.valid) return { valid: false };
        return {
            valid: true,
            sellable: data.sellable === "sellable" ? "sellable" : "unsellable",
            displayName: typeof data.displayName === "string" && data.displayName
                ? data.displayName
                : undefined,
        };
    };

    type CompleteTotePutAwayResult =
        | { kind: "ok" }
        | { kind: "error" }
        | { kind: "occupied" }
        | { kind: "wrong_location" }
        | { kind: "ppd_failed" };

    // `expectedLocation`/`requireEmpty` are Cold Putaway-only: they ask the
    // BE to re-verify the scan matches the suggested location and that it's
    // still empty, inside the same transaction (see completePutAway's
    // header comment BE-side). Omitted, behaviour is identical to before.
    const completeTotePutAway = useCallback(async (
        locationId: string,
        opts?: { expectedLocation?: string; requireEmpty?: boolean; toteId?: string },
    ): Promise<CompleteTotePutAwayResult> => {
        // toteId override: the "Stock already here" flow releases the tote
        // it FOUND in the location, not the one the operator is carrying.
        const currentTote = opts?.toteId ?? toteIdRef.current;
        if (!currentTote) return { kind: "error" };
        const res = await apiFetch(`/put-away/complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: currentTote,
                locationId,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
                ...(opts?.expectedLocation ? { expectedLocation: opts.expectedLocation } : {}),
                ...(opts?.requireEmpty ? { requireEmpty: true } : {}),
            }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { kind: "error" }; }
        if (res.ok) return { kind: "ok" };
        try {
            const body = await res.json();
            if (body?.reason === "ppd_failed") return { kind: "ppd_failed" };
            if (body?.reason === "location_occupied") return { kind: "occupied" };
            if (body?.reason === "wrong_location") return { kind: "wrong_location" };
        } catch {
            // not JSON / no body — fall through to generic error
        }
        return { kind: "error" };
    }, [navigate, userObject.uid, userObject.name, userObject.warehouse]);

    const completeItemPutAway = useCallback(async (locationId: string, itemBarcode: string) => {
        const currentTote = toteIdRef.current;
        if (!currentTote) return null;
        const res = await apiFetch(`/put-away/item/complete`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                toteId: currentTote,
                itemBarcode,
                locationId,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return null; }
        if (!res.ok) return null;
        const data = await res.json();
        return data as { success: boolean; remaining: string[]; toteEmpty: boolean };
    }, [navigate, userObject.uid, userObject.name, userObject.warehouse]);

    // Set by Consolidate's "Close & Put Away": once the ordinary put-away
    // of that tote finishes (or is abandoned), go back to the Consolidate
    // view rather than leaving the operator on plain Put Away (per Alex,
    // 2026-09-22). Consumed by the first resetToStart after the hand-off.
    const returnToConsolidateRef = useRef(false);
    const returnToConsolidate = () => {
        returnToConsolidateRef.current = false;
        setMode("high-empty");
        modeRef.current = "high-empty";
        setHighViewState("consolidate");
        setScanValue("");
    };
    const resetToStart = () => {
        setToteId(null);
        setToteType(null);
        toteTypeRef.current = null;
        setRemaining([]);
        itemMetaByBarcodeRef.current = {};
        clearPickedItem();
        setPhase("awaiting-tote");
        timer.reset();
        if (returnToConsolidateRef.current) returnToConsolidate();
    };

    // True while the unsellable-confirm modal is mid-call so the buttons
    // can be disabled and a duplicate confirm click doesn't double-submit.
    const [confirmingUnsellable, setConfirmingUnsellable] = useState(false);

    const cancelUnsellable = () => {
        if (confirmingUnsellable) return;
        setUnsellableConfirm(null);
        // Restart the timer so the operator's next scan is timed fresh.
        timer.start(computeNextDurationMs());
    };

    const cancelToteConfirm = () => {
        if (confirmingToteRef.current) return;
        toteConfirmRef.current = null;
        setToteConfirm(null);
        // The timer kept running while the prompt was open (so a confirmed
        // put-away still awards the remaining seconds) — restart it fresh
        // for the operator's next scan.
        timer.stop();
        timer.start(computeNextDurationMs());
    };

    // Plain function (not useCallback) for the same reason doSubmit is: it
    // runs from scan interception and the modal button, and must see the
    // current render's timer/flash closures — a memoised version kept an
    // initial-render stopTimerAndAward whose timer.state.active was false,
    // silently killing the score award.
    const confirmTotePutAway = async () => {
        const pending = toteConfirmRef.current;
        if (!pending || confirmingToteRef.current) return;
        confirmingToteRef.current = true;
        setConfirmingTote(true);
        try {
            const result = await completeTotePutAway(pending.locationId);
            if (result.kind === "ok") {
                stopTimerAndAward();
                playToteFinalised();
                flashStatus("final");
                resetToStart();
            } else if (result.kind === "ppd_failed") {
                playError();
                flashStatus("error");
                toast.error(t("ppd_sync_failed"), { toastId: TOAST_ID.ppdFailed });
                resetToStart();
            } else {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            }
        } catch {
            // Network failure — this runs outside doSubmit's try/catch (scan
            // interception and the modal button both call it directly), so
            // it must produce its own feedback. The prompt closes; rescanning
            // the location re-opens it for a retry.
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            confirmingToteRef.current = false;
            setConfirmingTote(false);
            toteConfirmRef.current = null;
            setToteConfirm(null);
        }
    };

    // Operator confirmed an item-level move to an unsellable location.
    const confirmUnsellable = useCallback(async () => {
        const pending = unsellableConfirm;
        if (!pending || confirmingUnsellable) return;
        setConfirmingUnsellable(true);
        try {
            const result = await completeItemPutAway(pending.locationId, pending.itemBarcode);
            if (!result || !result.success) {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            setRemaining(result.remaining);
            if (pickedItemMetaRef.current) setLastPutAway(pickedItemMetaRef.current);
            clearPickedItem();
            if (result.toteEmpty) {
                stopTimerAndAward();
                playToteFinalised();
                flashStatus("final");
                resetToStart();
            } else {
                playSuccess();
                setPhase("awaiting-location");
                timer.stop();
                timer.start(computeNextDurationMs());
            }
        } finally {
            setConfirmingUnsellable(false);
            setUnsellableConfirm(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unsellableConfirm, confirmingUnsellable, completeItemPutAway, t]);

    // Rescanning the open tote closes the put-away session and returns to
    // awaiting-tote. Tote itself stays in its 'closed' status in the DB —
    // this is a local UI session close, not a DB state change.
    const closeTote = () => {
        resetToStart();
        playBoxAdd();
        toast.info(t("put_away_tote_closed"), { toastId: TOAST_ID.toteClosed });
    };

    // Empty High Locations scan. Two states:
    //   • no suggestion on screen → the scan is the anchor: any HIGH
    //     location is accepted and the nearest empty one forward is shown.
    //   • suggestion on screen → only THAT location is accepted (arrival
    //     confirmation); it becomes the new anchor and the next one is
    //     shown. Anything else errors — "Change location" is the way to
    //     re-anchor elsewhere (per Alex, 2026-08-26).
    const handleHighEmptyScan = async (val: string) => {
        // The "doesn't exist" prompt owns the screen: a scan now would move
        // the card behind it and the confirm would mark the wrong location.
        if (highMissingRef.current) {
            setScanValue("");
            return;
        }
        const current = highEmptyRef.current;
        let visited = highVisitedRef.current;
        if (current?.next) {
            if (val !== current.next.barcode) {
                setScanValue("");
                playError();
                flashStatus("error");
                toast.error(
                    t("put_away_high_wrong_location", {
                        location: current.next.display,
                    }),
                    { toastId: TOAST_ID.highWrongLocation },
                );
                return;
            }
            visited = [...visited, current.next.barcode];
        }
        setLoading(true);
        try {
            // An arrival (the scan matched the suggestion) is what clears
            // the shelf for everyone; an anchor scan says nothing about it.
            const lookup = await lookupNextEmptyHigh(val, visited, {
                skipCleared: true,
                ...(current?.next ? { arrivedAt: val } : {}),
            });
            setScanValue("");
            if (!lookup.valid) {
                // Keep the current suggestion on screen — a bad scan
                // shouldn't wipe where the operator is heading.
                playError();
                flashStatus("error");
                toast.error(
                    lookup.reason === "not_high" ? t("put_away_high_not_high") : t("invalid_location"),
                    { toastId: lookup.reason === "not_high" ? TOAST_ID.highNotHigh : TOAST_ID.invalidLocation },
                );
                return;
            }
            // Only commit the visited hop once the BE has accepted it, so a
            // failed round trip leaves the session exactly as it was.
            highVisitedRef.current = visited;
            highEmptyRef.current = lookup.result;
            setHighEmpty(lookup.result);
            setHighEmptySeq((n) => n + 1);
            if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
            setScanStatus("idle");
            // The card is the feedback (same reasoning as category mode);
            // the sound says whether there is somewhere to go.
            if (lookup.result.next) playSuccess();
            else playError();
        } catch {
            setScanValue("");
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setLoading(false);
        }
    };

    // "Change location": drop the suggestion so the next scan re-anchors
    // anywhere. Visited locations stay excluded — nothing changed there.
    const changeHighLocation = () => {
        highEmptyRef.current = null;
        setHighEmpty(null);
        setScanValue("");
    };

    // "Location doesn't exist" (per Alex, 2026-09-15): the suggested slot
    // isn't physically there. Confirm first; on confirm the BE marks it
    // unsellable so it is never suggested again and hands back the next
    // suggestion from the same anchor, so the operator just keeps walking.
    const openHighMissing = () => {
        const next = highEmptyRef.current?.next;
        if (!next) return;
        highMissingRef.current = next;
        setHighMissingConfirm(next);
    };
    const cancelHighMissing = () => {
        if (confirmingHighMissing) return;
        highMissingRef.current = null;
        setHighMissingConfirm(null);
    };
    const confirmHighMissing = async () => {
        const missing = highMissingRef.current;
        const current = highEmptyRef.current;
        if (!missing || !current || confirmingHighMissing) return;
        setConfirmingHighMissing(true);
        try {
            const res = await apiFetch(`/put-away/high-empty/missing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    locationId: missing.barcode,
                    anchorId: current.anchor.barcode,
                    exclude: highVisitedRef.current,
                    skipCleared: true,
                }),
                signal: getSignal(),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = res.ok ? await res.json() : null;
            if (!data?.marked) {
                playError();
                toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                return;
            }
            highMissingRef.current = null;
            setHighMissingConfirm(null);
            toast.success(t("put_away_high_missing_done", { location: missing.display }), {
                toastId: TOAST_ID.highMissingDone,
            });
            if (!data.valid || !data.anchor) {
                // Marked, but the anchor no longer works as a start point:
                // drop the suggestion and let the next scan re-anchor.
                changeHighLocation();
                return;
            }
            const result: HighEmptyState = {
                anchor: shapeHighLocation(data.anchor),
                next: data.next ? shapeHighLocation(data.next) : null,
            };
            highEmptyRef.current = result;
            setHighEmpty(result);
            setHighEmptySeq((n) => n + 1);
            setScanValue("");
            if (result.next) playSuccess();
            else playError();
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setConfirmingHighMissing(false);
        }
    };

    // ───────────────────────── Consolidate ─────────────────────────
    // POST helper: identity travels in the body (dev bypass / body-fallback
    // path, same as Move Stock); in prod it's on the session.
    const consPost = async (path: string, body: Record<string, unknown>) => {
        const res = await apiFetch(path, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ...body,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return null; }
        return res;
    };

    const resetConsolidate = () => {
        setConsState(CONS_INITIAL);
        consDoneRef.current = [];
        setConsChangeOpenState(false);
        setConsFullOpenState(false);
        setConsReopenState(null);
        setConsCloseState(null);
        setConsSwapState(null);
        setConsBookInState(null);
        setConsWriteOffState(null);
    };

    // The operator's own open Consolidate tote, if any (Move Stock parity:
    // leaving the screen keeps the session in_progress). null = none.
    type ConsActive = { sessionId: number; toteId: string; movedUnits: number };
    const fetchConsolidateActive = async (): Promise<ConsActive | null> => {
        try {
            const params = new URLSearchParams();
            if (userObject.uid) params.set("uid", String(userObject.uid));
            if (userObject.warehouse) params.set("warehouse", userObject.warehouse);
            const res = await apiFetch(`/put-away/consolidate/active?${params.toString()}`, { signal: getSignal() });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return null; }
            const d = res.ok ? await res.json().catch(() => null) : null;
            const active = d?.active;
            if (!active?.toteId || !mountedRef.current) return null;
            const lines = Array.isArray(active.lines) ? active.lines : [];
            const movedUnits = lines.length > 0
                ? lines.reduce((a: number, l: { moved_qty?: unknown }) => a + Number(l.moved_qty ?? 0), 0)
                : Number(active.movedUnits ?? 0);
            return {
                sessionId: typeof active.sessionId === "number" ? active.sessionId : Number(active.sessionId),
                toteId: String(active.toteId),
                movedUnits,
            };
        } catch {
            return null;
        }
    };
    // Drop the operator back onto their open tote, at "scan where you are".
    const applyConsolidateActive = (active: ConsActive) => {
        setConsState({
            ...CONS_INITIAL,
            step: "anchor",
            sessionId: active.sessionId,
            toteId: active.toteId,
            movedUnits: active.movedUnits,
        });
    };
    const loadConsolidateActive = async (): Promise<boolean> => {
        const active = await fetchConsolidateActive();
        if (!active) return false;
        applyConsolidateActive(active);
        return true;
    };

    // Unfinished-session prompt on entry (Shelf Drop's model, per Alex,
    // 2026-09-22 — landing straight on "scan location" with a tote they
    // had forgotten about read as the tote-scan step being skipped).
    // Resume → Consolidate at scan-location; Close tote → to put-away, stay
    // on plain Put Away; dismiss → plain Put Away with the session left
    // open (Consolidate resumes it whenever they next go in). Scans are
    // swallowed while it is up.
    const [consResume, setConsResume] = useState<ConsActive | null>(null);
    const consResumePromptRef = useRef<ConsActive | null>(null);
    const setConsResumeState = (v: ConsActive | null) => { consResumePromptRef.current = v; setConsResume(v); };
    const resumeConsolidate = () => {
        const active = consResumePromptRef.current;
        if (!active || consBusy) return;
        setConsResumeState(null);
        applyConsolidateActive(active);
        setMode("high-empty");
        modeRef.current = "high-empty";
        setHighViewState("consolidate");
        setScanValue("");
        toast.info(t("put_away_consolidate_resumed", { tote: active.toteId }), { toastId: TOAST_ID.consResumed });
    };
    const dismissConsolidateResume = () => {
        if (consBusy) return;
        setConsResumeState(null);
    };
    const closeConsolidateFromResume = async () => {
        const active = consResumePromptRef.current;
        if (!active || consBusy) return;
        setConsBusy(true);
        try {
            const res = await consPost(`/put-away/consolidate/complete-tote`, { sessionId: active.sessionId });
            if (!res) return;
            const data = res.ok ? await res.json().catch(() => null) : null;
            if (!data?.success) {
                if (data?.reason === "not_active") {
                    // Already closed elsewhere — nothing left to do.
                    setConsResumeState(null);
                    toast.error(t("move_stock_session_ended"), { toastId: TOAST_ID.consEnded });
                    return;
                }
                playError();
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            setConsResumeState(null);
            toast.info(
                t(data.empty === true ? "put_away_consolidate_closed_empty_toast" : "put_away_consolidate_closed_toast", {
                    tote: active.toteId,
                }),
                { toastId: TOAST_ID.consClosed },
            );
            if (data.empty === true) playBoxAdd();
            else playToteFinalised();
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    // Empty ↔ Consolidate, the toggle inside the Empty High Locations
    // pill. Locked while a Consolidate tote is in hand (the button is
    // hidden then too) — Tote full is the way out.
    const switchHighView = async (v: HighView) => {
        if (v === highViewRef.current) return;
        if (consRef.current.toteId) return;
        // Mid "Stock already here" report (a check may be open) — the
        // toggle is hidden then too.
        if (foundRef.current) return;
        setHighViewState(v);
        highEmptyRef.current = null;
        highVisitedRef.current = [];
        setHighEmpty(null);
        highMissingRef.current = null;
        setHighMissingConfirm(null);
        resetConsolidate();
        setScanValue("");
        if (v === "consolidate") {
            setLoading(true);
            try {
                if (await loadConsolidateActive()) {
                    toast.info(t("put_away_consolidate_resumed", { tote: consRef.current.toteId ?? "" }), {
                        toastId: TOAST_ID.consResumed,
                    });
                }
            } finally {
                if (mountedRef.current) setLoading(false);
            }
        }
    };

    type ConsNext =
        | { valid: false; reason?: string }
        | { valid: true; anchor: HighLocation; next: ConsolidateTarget | null };
    const shapeConsNext = (data: any): ConsNext => {
        if (!data?.valid || !data.anchor) return { valid: false, reason: data?.reason };
        return {
            valid: true,
            anchor: shapeHighLocation(data.anchor),
            next: data.next ? shapeConsolidateTarget(data.next) : null,
        };
    };
    const fetchConsolidateNext = async (anchorBarcode: string, includeAnchor = false): Promise<ConsNext | null> => {
        const res = await consPost(`/put-away/consolidate/next`, {
            locationId: anchorBarcode,
            exclude: consDoneRef.current,
            ...(includeAnchor ? { includeAnchor: true } : {}),
        });
        if (!res) return null;
        // A 5xx is a broken backend, not a bad scan — name it as such.
        if (!res.ok) return { valid: false, reason: "server_error" };
        return shapeConsNext(await res.json().catch(() => null));
    };

    // Land on the BE's answer: a suggestion → "Go to" it; none → back to
    // "scan where you are" (the card is the feedback; the sound says
    // whether there is somewhere to go — same as Empty High Locations).
    // Toast for a lookup that didn't produce a suggestion.
    const consLookupErrorToast = (reason: string | undefined) => {
        toast.error(
            reason === "not_high" ? t("put_away_high_not_high")
                : reason === "not_available" ? t("put_away_consolidate_not_available")
                : reason === "server_error" ? t("server_error")
                : t("invalid_location"),
            {
                toastId: reason === "not_high" ? TOAST_ID.highNotHigh
                    : reason === "server_error" ? TOAST_ID.serverError
                    : TOAST_ID.invalidLocation,
            },
        );
    };
    const applyConsolidateNext = (result: ConsNext) => {
        if (!result.valid) {
            setConsState((p) => ({ ...p, step: "anchor", target: null }));
            playError();
            flashStatus("error");
            consLookupErrorToast(result.reason);
            return;
        }
        if (result.next) {
            setConsState((p) => ({ ...p, step: "location", anchor: result.anchor, target: result.next }));
            setConsSeq((n) => n + 1);
            if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
            setScanStatus("idle");
            playSuccess();
        } else {
            setConsState((p) => ({ ...p, step: "anchor", anchor: result.anchor, target: null }));
            playError();
            toast.info(t("put_away_consolidate_none"), { toastId: TOAST_ID.consNone });
        }
    };

    // The session was completed / abandoned elsewhere (another device, an
    // admin release): drop the tote, keep our place in the walk.
    const consolidateSessionEnded = () => {
        setConsState((p) => ({ ...p, step: "tote", sessionId: null, toteId: null, movedUnits: 0 }));
        setConsChangeOpenState(false);
        setConsFullOpenState(false);
        setConsCloseState(null);
        setConsSwapState(null);
        setConsBookInState(null);
        playError();
        flashStatus("error");
        toast.error(t("move_stock_session_ended"), { toastId: TOAST_ID.consEnded });
    };

    // Why the BE would not give us this tote — shared by open and Change Tote.
    const consToteRejectMessage = (data: any, scanned: string): string => {
        const reason: string = data?.reason ?? "";
        const otherScreen = data?.source === "move_stock";
        return reason === "not_available" ? t("put_away_consolidate_not_available")
            : reason === "locked" && otherScreen ? t("put_away_consolidate_tote_in_move_stock", { tote: scanned })
            : reason === "locked" ? t("move_stock_tote_locked", { name: data.openedBy || "" })
            : reason === "user_has_open_tote" && otherScreen
                ? t("put_away_consolidate_tote_in_move_stock", { tote: data.openToteId || "" })
            : reason === "user_has_open_tote" ? t("move_stock_user_has_open_tote", { tote: data.openToteId || "" })
            : reason === "status_open" ? t("move_stock_tote_open")
            : reason === "status_closed" || reason === "closed_goods_in" ? t("move_stock_tote_closed")
            : reason === "status_retired" ? t("tote_status_retired")
            : t("not_a_tote");
    };

    const consolidateOpenTote = async (val: string, reopen = false) => {
        const res = await consPost(`/put-away/consolidate/open-tote`, {
            toteId: val,
            ...(reopen ? { reopen: true } : {}),
        });
        if (!res) return;
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!data) {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (!data.valid) {
            if (data.reason === "closed_reopenable") {
                // A tote this screen (or Move Stock) closed earlier, still
                // waiting in Put Away — offer to carry on filling it.
                setConsReopenState({ tote: val, units: Number(data.movedUnits ?? 0) || 0, mode: "start" });
                return;
            }
            playError();
            flashStatus("error");
            toast.error(consToteRejectMessage(data, val), { toastId: TOAST_ID.consTote });
            return;
        }
        const lines = Array.isArray(data.lines) ? data.lines : [];
        const movedUnits = lines.reduce((a: number, l: { moved_qty?: unknown }) => a + Number(l.moved_qty ?? 0), 0);
        // A suggestion left on screen when the last tote was closed is
        // still where they are heading — a new tote picks the walk up there.
        setConsState((p) => ({
            ...p,
            step: p.target ? "location" : "anchor",
            sessionId: typeof data.sessionId === "number" ? data.sessionId : Number(data.sessionId),
            toteId: String(data.toteId ?? val),
            movedUnits,
        }));
        playBoxAdd();
    };
    const cancelConsReopen = () => {
        if (consBusy) return;
        setConsReopenState(null);
    };
    const confirmConsReopen = async () => {
        const r = consReopenRef.current;
        if (!r || consBusy) return;
        setConsBusy(true);
        try {
            setConsReopenState(null);
            if (r.mode === "swap") await consolidateSwapTo(r.tote, true);
            else await consolidateOpenTote(r.tote, true);
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) {
                setConsBusy(false);
                setScanValue("");
            }
        }
    };

    const consolidateAnchor = async (val: string) => {
        const result = await fetchConsolidateNext(val);
        if (!result) return;
        if (!result.valid) {
            // Not a location at all? It may be a fresh tote — Change Tote.
            if (result.reason === "not_a_location" && await consolidateTrySwap(val)) return;
            // A bad anchor scan leaves the step where it is.
            playError();
            flashStatus("error");
            consLookupErrorToast(result.reason);
            return;
        }
        applyConsolidateNext(result);
    };

    // Next suggestion from the location just emptied / found empty.
    const consolidateAdvanceFrom = async (barcode: string) => {
        const result = await fetchConsolidateNext(barcode);
        if (!result) return;
        applyConsolidateNext(result);
    };

    const consolidateArrive = async (val: string) => {
        const c = consRef.current;
        const res = await consPost(`/put-away/consolidate/open-location`, { sessionId: c.sessionId, location: val });
        if (!res) return;
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!data) {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (!data.valid) {
            if (data.reason === "not_active") { consolidateSessionEnded(); return; }
            playError();
            flashStatus("error");
            toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
            return;
        }
        const contents: ConsolidateContent[] = Array.isArray(data.contents) ? data.contents.map(shapeConsolidateContent) : [];
        if (contents.length === 0) {
            // Picked clean since it was suggested — not the operator's
            // fault; carry on from here.
            consDoneRef.current = [...consDoneRef.current, val];
            playBoxAdd();
            toast.info(t("put_away_consolidate_location_empty"), { toastId: TOAST_ID.consLocationEmpty });
            await consolidateAdvanceFrom(val);
            return;
        }
        setConsState((p) => ({
            ...p,
            step: "items",
            target: p.target ? { ...p.target, contents, units: contents.reduce((a, r) => a + r.qty, 0) } : p.target,
        }));
        playBoxAdd();
    };

    // A unit went onto the tote (scan, or book-in-and-move): update the
    // rows, the count, say if a parked tote was freed, and move on when the
    // location is now empty. A SKU the rows didn't have (booked in) gets
    // its own row, already done.
    const applyConsolidateMatched = async (data: any, location: string) => {
        const moved = Number(data.movedForSku ?? 1) || 1;
        setConsState((p) => {
            if (!p.target) return { ...p, movedUnits: p.movedUnits + moved };
            const has = p.target.contents.some((row) => row.sku === data.sku);
            // A unit beyond what the data had (booked in on the spot) grows
            // the row's total, so the counter reads 3 / 3 not 2 / 2.
            const contents = has
                ? p.target.contents.map((row) => {
                    if (row.sku !== data.sku) return row;
                    const take = Math.min(row.qty, moved);
                    return { ...row, qty: row.qty - take, startQty: row.startQty + (moved - take) };
                })
                : [...p.target.contents, {
                    sku: String(data.sku ?? ""),
                    title: typeof data.title === "string" && data.title ? data.title : String(data.sku ?? ""),
                    barcode: typeof data.barcode === "string" && data.barcode ? data.barcode : null,
                    thumbnailUrl: null,
                    qty: 0,
                    startQty: moved,
                }];
            return { ...p, movedUnits: p.movedUnits + moved, target: { ...p.target, contents } };
        });
        const released: string[] = Array.isArray(data.releasedTotes) ? data.releasedTotes.map(String) : [];
        if (released.length > 0) {
            // A whole tote parked here (Cold Putaway) is now empty — it
            // goes with them, not left on the racking.
            toast.info(t("put_away_consolidate_take_tote", { totes: released.join(", ") }), {
                toastId: TOAST_ID.consTakeTote,
                autoClose: 8000,
            });
        }
        playSuccess();
        // Deliberately NO auto-advance when the data says the location is
        // now empty (Kyle, 2026-09-22: three of one SKU on the shelf, two
        // booked in — the walk moved him on after the second and the third
        // could never be booked in). The operator says when they are done,
        // with Next location; until then any further scan here goes down
        // the "not recorded here → book it in and move it" path.
    };

    // "Next location": done here (whatever the data says is left) — this
    // location joins the session's exclude list and the walk carries on
    // from it.
    const consolidateNextLocation = async () => {
        const c = consRef.current;
        if (!c.target || c.step !== "items" || consBusy) return;
        const from = c.target.barcode;
        setConsBusy(true);
        try {
            consDoneRef.current = [...consDoneRef.current, from];
            await consolidateAdvanceFrom(from);
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    // "Book it in and move it": confirmed from the not-here prompt.
    const cancelConsBookIn = () => {
        if (consBusy) return;
        setConsBookInState(null);
    };
    const confirmConsBookIn = async () => {
        const c = consRef.current;
        const prompt = consBookInRef.current;
        if (!prompt || !c.target || !c.sessionId || consBusy) return;
        const location = c.target.barcode;
        setConsBusy(true);
        try {
            const res = await consPost(`/put-away/consolidate/book-in-move`, {
                sessionId: c.sessionId, location, barcode: prompt.barcode,
            });
            if (!res) return;
            const data = res.ok ? await res.json().catch(() => null) : null;
            setConsBookInState(null);
            if (!data) {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            if (data.valid === false && data.reason === "not_active") { consolidateSessionEnded(); return; }
            if (data.outcome === "matched") {
                toast.success(t("put_away_consolidate_booked_in", { title: prompt.title }), {
                    toastId: TOAST_ID.consBookedIn,
                });
                await applyConsolidateMatched({ ...data, barcode: prompt.barcode }, location);
                return;
            }
            playError();
            flashStatus("error");
            toast.error(
                data.outcome === "on_wave" ? t("put_away_consolidate_on_wave")
                    : data.outcome === "reserved" ? t("put_away_consolidate_reserved")
                    : data.outcome === "none_left" ? t("put_away_consolidate_none_left")
                    : data.outcome === "unknown" ? t("scan_not_recognised")
                    : t("server_error"),
                { toastId: TOAST_ID.consScan },
            );
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    const consolidateScanItem = async (val: string) => {
        const c = consRef.current;
        if (!c.target) return;
        const location = c.target.barcode;
        const res = await consPost(`/put-away/consolidate/scan`, { sessionId: c.sessionId, location, barcode: val });
        if (!res) return;
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!data) {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (data.valid === false && data.reason === "not_active") { consolidateSessionEnded(); return; }
        switch (data.outcome) {
            case "matched":
                await applyConsolidateMatched(data, location);
                return;
            case "not_here":
                // A real product the data doesn't have here — offer to
                // book it in and move it, rather than a dead-end error.
                if (typeof data.sku === "string" && data.sku) {
                    playBoxAdd();
                    setConsBookInState({
                        sku: data.sku,
                        title: typeof data.title === "string" && data.title ? data.title : data.sku,
                        barcode: val,
                    });
                    return;
                }
                playError();
                flashStatus("error");
                toast.error(t("put_away_consolidate_not_here"), { toastId: TOAST_ID.consScan });
                return;
            case "on_wave":
                // A wave was built for this unit since the suggestion — a
                // picker is walking here for it. Leave it.
                playError();
                flashStatus("error");
                toast.error(t("put_away_consolidate_on_wave"), { toastId: TOAST_ID.consScan });
                return;
            case "reserved":
                // An order for this SKU arrived since the suggestion and
                // this is the last of it on the shelf. Leave it.
                playError();
                flashStatus("error");
                toast.error(t("put_away_consolidate_reserved"), { toastId: TOAST_ID.consScan });
                return;
            case "none_left":
                setConsState((p) => ({
                    ...p,
                    target: p.target ? {
                        ...p.target,
                        contents: p.target.contents.map((row) => row.sku === data.sku ? { ...row, qty: 0 } : row),
                    } : p.target,
                }));
                playError();
                flashStatus("error");
                toast.error(t("put_away_consolidate_none_left"), { toastId: TOAST_ID.consScan });
                return;
            default:
                // Not a product at all? It may be a fresh tote — Change Tote.
                if (await consolidateTrySwap(val)) return;
                playError();
                flashStatus("error");
                toast.error(t("scan_not_recognised"), { toastId: TOAST_ID.unknownScan });
        }
    };

    // "Change location" — Picking's gesture: tap the pin, then scan the
    // location you are moving to. Pure navigation: the suggestion on screen
    // stays on the list (it will come round again), and the scan is the
    // new anchor — itself the answer when it qualifies.
    const openConsChange = () => {
        const c = consRef.current;
        if (!c.target || (c.step !== "location" && c.step !== "items")) return;
        setConsChangeOpenState(true);
        setScanValue("");
    };
    const cancelConsChange = () => {
        if (consBusy) return;
        setConsChangeOpenState(false);
    };
    const consolidateChangeTo = async (val: string) => {
        const result = await fetchConsolidateNext(val, true);
        if (!result) return;
        if (!result.valid) {
            // Keep the prompt open — they can scan another location or cancel.
            playError();
            flashStatus("error");
            consLookupErrorToast(result.reason);
            return;
        }
        setConsChangeOpenState(false);
        applyConsolidateNext(result);
    };

    // "Tote full" (per Alex, 2026-09-22): the units at this location are
    // big and bulky, not worth consolidating. Confirm, then the BE records
    // the skip against the location's current contents — off everyone's
    // list until something is picked from it — and the walk carries on
    // from this location.
    // "Not here" (per Alex, 2026-09-23): the data says this SKU is on the
    // shelf and the operator can't find it. Confirm, then the BE writes the
    // remaining recorded units off (shared write-off service, so it shows
    // in Write Off history) and hands back the location's real contents.
    const openConsWriteOff = (row: ConsolidateContent) => {
        const c = consRef.current;
        if (c.step !== "items" || !c.target || consBusy || row.qty <= 0) return;
        setConsWriteOffState(row);
        setScanValue("");
    };
    const cancelConsWriteOff = () => {
        if (consBusy) return;
        setConsWriteOffState(null);
    };
    const confirmConsWriteOff = async () => {
        const c = consRef.current;
        const row = consWriteOffRef.current;
        if (!row || !c.target || !c.sessionId || consBusy) return;
        const location = c.target.barcode;
        setConsBusy(true);
        try {
            const res = await consPost(`/put-away/consolidate/write-off`, {
                sessionId: c.sessionId, location, sku: row.sku,
            });
            if (!res) return;
            const data = res.ok ? await res.json().catch(() => null) : null;
            setConsWriteOffState(null);
            if (!data) {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            if (data.valid === false && data.reason === "not_active") { consolidateSessionEnded(); return; }
            if (data.outcome === "written_off") {
                const gone = Number(data.qty) || 0;
                setConsState((p) => {
                    if (!p.target) return p;
                    // The written-off units are no longer "to scan": the
                    // row's total shrinks by what went, its remainder is 0.
                    const contents = p.target.contents.map((r) => r.sku !== row.sku ? r : {
                        ...r, qty: 0, startQty: Math.max(0, r.startQty - gone),
                    });
                    return { ...p, target: { ...p.target, contents } };
                });
                const released: string[] = Array.isArray(data.releasedTotes) ? data.releasedTotes.map(String) : [];
                if (released.length > 0) {
                    toast.info(t("put_away_consolidate_take_tote", { totes: released.join(", ") }), {
                        toastId: TOAST_ID.consTakeTote,
                        autoClose: 8000,
                    });
                }
                playBoxAdd();
                toast.success(t("put_away_consolidate_written_off", { title: row.title }), {
                    toastId: TOAST_ID.consWrittenOff,
                });
                return;
            }
            playError();
            flashStatus("error");
            toast.error(
                data.outcome === "on_wave" ? t("put_away_consolidate_on_wave")
                    : data.outcome === "allocated" ? t("put_away_consolidate_write_off_allocated")
                    : data.outcome === "none_left" ? t("put_away_consolidate_none_left")
                    : t("server_error"),
                { toastId: TOAST_ID.consScan },
            );
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    const openConsFull = () => {
        const c = consRef.current;
        if (!c.toteId || !c.target || (c.step !== "location" && c.step !== "items")) return;
        setConsFullOpenState(true);
        setScanValue("");
    };
    const cancelConsFull = () => {
        if (consBusy) return;
        setConsFullOpenState(false);
    };
    const confirmConsFull = async () => {
        const c = consRef.current;
        if (!c.target || consBusy) return;
        const skippedLocation = c.target;
        setConsBusy(true);
        try {
            const res = await consPost(`/put-away/consolidate/skip`, {
                location: skippedLocation.barcode,
                anchorId: skippedLocation.barcode,
                exclude: consDoneRef.current,
            });
            if (!res) return;
            const data = res.ok ? await res.json().catch(() => null) : null;
            if (!data) {
                playError();
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            if (!data.valid && data.reason === "not_available") {
                playError();
                toast.error(t("put_away_consolidate_not_available"), { toastId: TOAST_ID.consTote });
                return;
            }
            setConsFullOpenState(false);
            consDoneRef.current = [...consDoneRef.current, skippedLocation.barcode];
            toast.success(t("put_away_consolidate_skipped", { location: skippedLocation.display }), {
                toastId: TOAST_ID.consSkipped,
            });
            applyConsolidateNext(shapeConsNext(data));
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    // Close Tote (Shelf Drop): the first rescan of the tote in hand only
    // opens the confirm; the same tote rescanned, or the button, closes it
    // to the Put Away queue and ends the walk.
    const openConsClose = () => {
        const c = consRef.current;
        if (!c.toteId) return;
        playBoxAdd();
        setConsCloseState({ toteId: c.toteId, units: c.movedUnits });
        setScanValue("");
    };
    const cancelConsClose = () => {
        if (consBusy) return;
        setConsCloseState(null);
    };
    // `putAwayNow` (Book In's "Complete & Putaway" parity, per Alex,
    // 2026-09-22): after closing, switch to plain Put Away and claim this
    // tote so the operator puts it away right now instead of it waiting
    // in the queue. A rescan of the tote (Shelf Drop's gesture) closes to
    // the queue; the button chooses.
    const confirmConsClose = async (putAwayNow = false) => {
        const c = consRef.current;
        const prompt = consCloseRef.current;
        if (!prompt || !c.sessionId || consBusy) return;
        setConsBusy(true);
        try {
            const res = await consPost(`/put-away/consolidate/complete-tote`, { sessionId: c.sessionId });
            if (!res) return;
            const data = res.ok ? await res.json().catch(() => null) : null;
            if (!data?.success) {
                if (data?.reason === "not_active") { consolidateSessionEnded(); return; }
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                setConsCloseState(null);
                return;
            }
            // The walk ends with the tote (Shelf Drop: "This ends your
            // drop session") — the next scan starts a fresh one.
            resetConsolidate();
            if (putAwayNow && data.empty !== true) {
                // Straight into Put Away with this tote in hand — the same
                // silent claim Book In's hand-off uses.
                setMode("put-away");
                modeRef.current = "put-away";
                setHighViewState("empty");
                playToteFinalised();
                returnToConsolidateRef.current = true;
                await doSubmit(prompt.toteId, { silentClaim: true });
                // Claim refused (someone else grabbed it from the queue,
                // network…): the tote is queued anyway — back to Consolidate.
                if (!toteIdRef.current) returnToConsolidate();
                return;
            }
            toast.info(
                t(data.empty === true ? "put_away_consolidate_closed_empty_toast" : "put_away_consolidate_closed_toast", {
                    tote: prompt.toteId,
                }),
                { toastId: TOAST_ID.consClosed },
            );
            if (data.empty === true) {
                playBoxAdd();
            } else {
                playToteFinalised();
                flashStatus("final");
            }
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            setConsCloseState(null);
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };

    // Change Tote (Shelf Drop): a scan mid-walk that is neither the
    // expected location nor a product may be a fresh tote. Ask the BE to
    // dry-run the swap; if it would work, show the confirm prompt. Returns
    // true when the scan was consumed (prompt opened, or a tote-specific
    // rejection shown), false when it isn't a tote at all so the caller
    // falls through to its usual wrong-scan error.
    const consolidateTrySwap = async (val: string): Promise<boolean> => {
        const c = consRef.current;
        if (!c.sessionId || !c.toteId) return false;
        const res = await consPost(`/put-away/consolidate/swap-tote`, {
            sessionId: c.sessionId, newToteId: val, check: true,
        });
        if (!res) return true;
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!data) return false;
        if (data.valid) {
            playBoxAdd();
            setConsSwapState({ toteId: String(data.toteId ?? val) });
            return true;
        }
        if (data.reason === "not_a_tote" || data.reason === "same_tote") return false;
        if (data.reason === "not_active") { consolidateSessionEnded(); return true; }
        if (data.reason === "closed_reopenable") {
            setConsReopenState({ tote: val, units: Number(data.movedUnits ?? 0) || 0, mode: "swap" });
            return true;
        }
        playError();
        flashStatus("error");
        toast.error(consToteRejectMessage(data, val), { toastId: TOAST_ID.consTote });
        return true;
    };
    const cancelConsSwap = () => {
        if (consBusy) return;
        setConsSwapState(null);
    };
    // The real swap: the current tote goes to put-away (or is released if
    // empty), the new one opens, and the walk keeps its place.
    const consolidateSwapTo = async (toteId: string, reopen = false) => {
        const c = consRef.current;
        if (!c.sessionId) return;
        const res = await consPost(`/put-away/consolidate/swap-tote`, {
            sessionId: c.sessionId, newToteId: toteId, ...(reopen ? { reopen: true } : {}),
        });
        if (!res) return;
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (!data) {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (!data.valid) {
            if (data.reason === "not_active") { consolidateSessionEnded(); return; }
            if (data.reason === "closed_reopenable") {
                setConsReopenState({ tote: toteId, units: Number(data.movedUnits ?? 0) || 0, mode: "swap" });
                return;
            }
            playError();
            flashStatus("error");
            toast.error(consToteRejectMessage(data, toteId), { toastId: TOAST_ID.consTote });
            return;
        }
        const lines = Array.isArray(data.lines) ? data.lines : [];
        const movedUnits = lines.reduce((a: number, l: { moved_qty?: unknown }) => a + Number(l.moved_qty ?? 0), 0);
        const closedTote = String(data.closed?.toteId ?? c.toteId ?? "");
        setConsState((p) => ({
            ...p,
            sessionId: typeof data.sessionId === "number" ? data.sessionId : Number(data.sessionId),
            toteId: String(data.toteId ?? toteId),
            movedUnits,
        }));
        toast.info(
            t(data.closed?.empty === true ? "put_away_consolidate_closed_empty_toast" : "put_away_consolidate_closed_toast", {
                tote: closedTote,
            }),
            { toastId: TOAST_ID.consClosed },
        );
        playToteFinalised();
        flashStatus("success");
    };
    const confirmConsSwap = async () => {
        const prompt = consSwapRef.current;
        if (!prompt || consBusy) return;
        setConsBusy(true);
        try {
            setConsSwapState(null);
            await consolidateSwapTo(prompt.toteId);
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) setConsBusy(false);
        }
    };
    const handleConsolidateScan = async (val: string) => {
        // A confirm owns the screen (Tote full / Reopen tote? / Book it in? / Not here?).
        if (consFullOpenRef.current || consReopenRef.current || consBookInRef.current || consWriteOffRef.current) {
            setScanValue("");
            return;
        }
        // Close / Change Tote confirms (Shelf Drop): only the SAME tote
        // rescanned confirms; anything else is rejected — Cancel is on screen.
        if (consCloseRef.current) {
            if (val === consCloseRef.current.toteId) await confirmConsClose();
            else { playError(); flashStatus("error"); }
            setScanValue("");
            return;
        }
        if (consSwapRef.current) {
            if (val === consSwapRef.current.toteId) await confirmConsSwap();
            else { playError(); flashStatus("error"); }
            setScanValue("");
            return;
        }
        setLoading(true);
        try {
            if (consChangeOpenRef.current) {
                await consolidateChangeTo(val);
                return;
            }
            const c = consRef.current;
            if (c.step === "tote") { await consolidateOpenTote(val); return; }
            if (c.step === "anchor") { await consolidateAnchor(val); return; }
            if (c.toteId && val === c.toteId) { openConsClose(); return; }
            if (c.step === "location") {
                if (!c.target || val !== c.target.barcode) {
                    // Tote full? A fresh tote here offers to change to it.
                    if (await consolidateTrySwap(val)) return;
                    playError();
                    flashStatus("error");
                    toast.error(
                        t("put_away_high_wrong_location", { location: c.target?.display ?? "" }),
                        { toastId: TOAST_ID.highWrongLocation },
                    );
                    return;
                }
                await consolidateArrive(val);
                return;
            }
            // items
            if (c.target && val === c.target.barcode) {
                // Habit rescan of the location label — harmless.
                playBoxAdd();
                return;
            }
            await consolidateScanItem(val);
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setScanValue("");
            }
        }
    };

    // Cold Putaway reuses the exact same "next empty location in walk
    // order" lookup Empty High Locations already calls — see put-away
    // router's high-empty/next comment and ColdStep's header comment.
    // Called without the navigator's skipCleared/arrivedAt flags on
    // purpose: Cold fills empty slots, so a shelf the Empties walk has
    // already cleared is a perfectly good target, and nothing here should
    // stamp one cleared.
    const lookupNextEmptyCold = lookupNextEmptyHigh;

    const startCold = () => {
        if (!isPembroke || toteId) return;
        setColdStep("anchor");
        coldStepRef.current = "anchor";
        setColdAnchor(null);
        setColdSuggested(null);
        coldSuggestedRef.current = null;
        coldExcludeRef.current = [];
        setScanValue("");
    };

    // Only reachable before a tote is claimed (see the button's render
    // guard) — once a tote is in hand, rescanning it is the way out,
    // exactly like the ordinary flow's closeTote.
    const exitCold = () => {
        setColdStep(null);
        coldStepRef.current = null;
        setColdAnchor(null);
        setColdSuggested(null);
        coldSuggestedRef.current = null;
        coldExcludeRef.current = [];
        setFoundState(null);
        setFoundFinishConfirmState(false);
        setScanValue("");
    };

    // Re-run the walk-order lookup from `anchorBarcode`, excluding
    // everything reported/visited so far this session, and land on
    // `landOn` with the answer. Shared by: a completed move (anchor = the
    // location just filled, land on "tote" for the next tote), the race
    // where completePutAway finds the suggested spot already occupied
    // (land on "location" — the SAME tote is still in hand, just needs a
    // new destination), and both report actions (land on "tote", nothing
    // claimed yet).
    const refreshColdSuggestion = async (anchorBarcode: string, landOn: "tote" | "location") => {
        setLoading(true);
        try {
            const lookup = await lookupNextEmptyCold(anchorBarcode, coldExcludeRef.current);
            if (!lookup.valid || !lookup.result.next) {
                setColdStep("anchor");
                coldStepRef.current = "anchor";
                setColdAnchor(null);
                setColdSuggested(null);
                coldSuggestedRef.current = null;
                toast.info(
                    lookup.valid ? t("put_away_high_none") : t("invalid_location"),
                    { toastId: TOAST_ID.coldNone },
                );
                return;
            }
            setColdAnchor(lookup.result.anchor);
            setColdSuggested(lookup.result.next);
            coldSuggestedRef.current = lookup.result.next;
            setColdStep(landOn);
            coldStepRef.current = landOn;
        } finally {
            setLoading(false);
        }
    };

    const openColdMissing = () => {
        if (coldSuggestedRef.current) setColdMissingConfirm(coldSuggestedRef.current);
    };
    const cancelColdMissing = () => {
        if (confirmingColdMissing) return;
        setColdMissingConfirm(null);
    };
    const confirmColdMissing = async () => {
        const missing = coldMissingConfirm;
        const anchor = coldAnchor;
        if (!missing || !anchor || confirmingColdMissing) return;
        setConfirmingColdMissing(true);
        try {
            const res = await apiFetch(`/put-away/high-empty/missing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    locationId: missing.barcode,
                    anchorId: anchor.barcode,
                    exclude: coldExcludeRef.current,
                }),
                signal: getSignal(),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const data = res.ok ? await res.json() : null;
            if (!data?.marked) {
                playError();
                toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                return;
            }
            setColdMissingConfirm(null);
            coldExcludeRef.current = [...coldExcludeRef.current, missing.barcode];
            toast.success(t("put_away_high_missing_done", { location: missing.display }), {
                toastId: TOAST_ID.highMissingDone,
            });
            if (!data.valid || !data.next) {
                setColdStep("anchor");
                coldStepRef.current = "anchor";
                setColdAnchor(null);
                setColdSuggested(null);
                coldSuggestedRef.current = null;
                return;
            }
            setColdAnchor(data.anchor ? shapeHighLocation(data.anchor) : anchor);
            const nextLoc = shapeHighLocation(data.next);
            setColdSuggested(nextLoc);
            coldSuggestedRef.current = nextLoc;
            setColdStep("tote");
            coldStepRef.current = "tote";
        } catch {
            playError();
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setConfirmingColdMissing(false);
        }
    };

    // ─── "Stock already here" ─────────────────────────────────────────
    // Entry from the Cold card. Only reachable at coldStep "tote" (nothing
    // in hand), so every exit lands back on "tote".
    const openColdOccupied = () => {
        const loc = coldSuggestedRef.current;
        if (!loc || foundRef.current) return;
        setFoundState({ location: loc, stage: "location", toteId: null, checkId: null, lines: [], added: 0 });
        setScanValue("");
    };

    // Entry from the Empty High Locations card (per Alex, 2026-09-23): the
    // same flow, for the same reason — the list says this shelf is empty
    // and there is a tote sat in it. Not while the "doesn't exist" confirm
    // is up: two reports about one location can't both be right.
    const openHighOccupied = () => {
        const loc = highEmptyRef.current?.next;
        if (!loc || foundRef.current || highMissingRef.current) return;
        setFoundState({ location: loc, stage: "location", toteId: null, checkId: null, lines: [], added: 0 });
        setScanValue("");
    };

    // Empty High Locations' continuation after a terminal branch: the
    // operator is stood at locationBarcode and it now has stock, so it is
    // an arrival — visited, and the next suggestion is walked from it.
    // Not sent as arrivedAt: the shelf has stock now (it is out of the
    // candidate set for the ordinary reason), and a cleared stamp would
    // wrongly hide it once that stock is picked down. Same success path as
    // handleHighEmptyScan; a failed lookup leaves the navigator un-anchored
    // (the next scan re-anchors) rather than pointing at the location just
    // filled.
    const advanceHighEmptyFrom = async (locationBarcode: string) => {
        const visited = highVisitedRef.current.includes(locationBarcode)
            ? highVisitedRef.current
            : [...highVisitedRef.current, locationBarcode];
        highVisitedRef.current = visited;
        setLoading(true);
        try {
            const lookup = await lookupNextEmptyHigh(locationBarcode, visited, { skipCleared: true });
            if (!lookup.valid) {
                highEmptyRef.current = null;
                setHighEmpty(null);
                return;
            }
            highEmptyRef.current = lookup.result;
            setHighEmpty(lookup.result);
            setHighEmptySeq((n) => n + 1);
        } catch {
            highEmptyRef.current = null;
            setHighEmpty(null);
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setLoading(false);
        }
    };

    // Undo the report — back to the suggestion card, location NOT excluded.
    // Not offered once a check is open (counting): Finish is the way out.
    const cancelFound = () => {
        if (foundBusy) return;
        setFoundState(null);
        setFoundFinishConfirmState(false);
        setScanValue("");
    };

    // Every terminal branch: the location is now spoken for (released tote,
    // or booked-in stock), skip it for the rest of the session and hand the
    // operator the next one — for the tote they're actually carrying (Cold),
    // or simply the next empty one down the route (Empty High Locations).
    const finishFoundAndMoveOn = async (locationBarcode: string) => {
        setFoundState(null);
        setFoundFinishConfirmState(false);
        if (modeRef.current === "high-empty") {
            await advanceHighEmptyFrom(locationBarcode);
            return;
        }
        coldExcludeRef.current = [...coldExcludeRef.current, locationBarcode];
        await refreshColdSuggestion(locationBarcode, "tote");
    };

    type Whereabouts = {
        valid: boolean;
        reason?: string;
        toteStatus?: string;
        goodsIn?: boolean;
        shelf?: unknown;
        forecast?: unknown;
        onWave?: boolean;
    };
    const lookupWhereabouts = async (val: string): Promise<Whereabouts> => {
        const res = await apiFetch(`/put-away/tote/whereabouts`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ toteId: val }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return { valid: false }; }
        if (!res.ok) return { valid: false };
        return await res.json();
    };

    // Open the real stock check for the location — see foundStockStart
    // BE-side. Lands on "counting" with the check's lines (normally none:
    // the data says this shelf is empty, which is the whole point).
    const startFoundCount = async (f: FoundState) => {
        const res = await apiFetch(`/put-away/found-stock/start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                location: f.location.barcode,
                uid: userObject.uid,
                name: userObject.name,
                warehouse: userObject.warehouse,
            }),
            signal: getSignal(),
        });
        if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
        const body = res.ok ? await res.json() : null;
        if (!body?.ok || !body.stock_check_id) {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            return;
        }
        if (body.took_over_from) {
            toast.info(t("picking_missing_took_over", { name: body.took_over_from }), { toastId: "pa-found-tookover" });
        }
        playBoxAdd();
        setFoundState({
            ...f,
            stage: "counting",
            checkId: Number(body.stock_check_id),
            lines: Array.isArray(body.stock_check_lines) ? body.stock_check_lines : [],
        });
    };

    // Release stage: commit the FOUND tote into this location. Deliberately
    // no requireEmpty — the premise is that stock is here.
    const releaseFoundTote = async () => {
        const f = foundRef.current;
        if (!f || f.stage !== "release" || !f.toteId || foundBusy) return;
        setFoundBusy(true);
        try {
            const result = await completeTotePutAway(f.location.barcode, {
                toteId: f.toteId,
                expectedLocation: f.location.barcode,
            });
            if (result.kind === "ok") {
                playToteFinalised();
                flashStatus("final");
                toast.success(
                    t("put_away_found_released", { tote: f.toteId, location: f.location.display }),
                    { toastId: "pa-found-released" },
                );
                await finishFoundAndMoveOn(f.location.barcode);
            } else if (result.kind === "ppd_failed") {
                playError();
                flashStatus("error");
                toast.error(t("ppd_sync_failed"), { toastId: TOAST_ID.ppdFailed });
            } else {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            }
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setFoundBusy(false);
        }
    };

    // Book one found unit into the location — through the Stock Check
    // module's own /stock-check/add (as Picking's and Drop's flows do) so it
    // is costed from the catalogue and recorded on the check's lines.
    // Called straight from the count scan for a unit the data doesn't have
    // here (or a further unit of one it does): the scan IS the yes. Runs
    // inside the caller's loading/try scope.
    const bookFoundUnit = async (p: { sku: string; title: string; barcode: string }) => {
        const f = foundRef.current;
        if (!f || f.checkId == null) return;
        {
            const res = await apiFetch(`/stock-check/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkId: f.checkId,
                    sku: p.sku,
                    qty: 1,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                }),
                signal: getSignal(),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            if (!res.ok) {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            playBoxAdd();
            const hit = f.lines.some((l) => l.sku === p.sku);
            const lines: StockCheckLine[] = hit
                ? f.lines.map((l) => (l.sku === p.sku ? { ...l, scanned_qty: l.scanned_qty + 1 } : l))
                : [...f.lines, {
                    sku: p.sku, title: p.title, barcode: p.barcode || null,
                    expected_qty: 0, scanned_qty: 1,
                }];
            setFoundState({ ...f, lines, added: f.added + 1 });
        }
    };

    // Units physically confirmed here this visit — matched expected stock
    // plus everything booked in — i.e. what would be left behind.
    const foundCountedUnits = (f: FoundState) => f.lines.reduce((a, l) => a + l.scanned_qty, 0);

    // Finish tapped on the count. Anything counted → straight to the
    // put-away location scan; nothing counted → confirm first (they said
    // stock was here and found none), then complete the check.
    const startFoundFinish = () => {
        const f = foundRef.current;
        if (!f || foundBusy) return;
        if (foundCountedUnits(f) > 0) {
            setFoundState({ ...f, stage: "destination" });
            setScanValue("");
            return;
        }
        setFoundFinishConfirmState(true);
    };

    // Complete the check. Nothing is written off (see completeCheckForPutAway
    // BE-side) — this flow only ever adds what was found. With a
    // `destination` other than this location the BE books the units in
    // here and relocates them there in the same call.
    const finishFound = async (destination?: string) => {
        const f = foundRef.current;
        if (!f || f.checkId == null || foundBusy) return;
        const moveTo = destination && destination !== f.location.barcode ? destination : null;
        setFoundBusy(true);
        try {
            const res = await apiFetch(`/put-away/found-stock/finish`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkId: f.checkId,
                    location: f.location.barcode,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                    ...(moveTo ? { destination: moveTo } : {}),
                }),
                signal: getSignal(),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const body = res.ok ? await res.json() : null;
            if (!body?.ok) {
                playError();
                flashStatus("error");
                // Destination refusals leave the check open and the operator
                // on the destination step: scan somewhere else, or keep here.
                if (body?.reason === "unknown_location" || body?.reason === "wrong_warehouse") {
                    toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                } else if (body?.reason === "on_wave") {
                    toast.error(t("put_away_found_dest_on_wave"), { toastId: "pa-found-on-wave" });
                } else {
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                }
                return;
            }
            playToteFinalised();
            flashStatus("final");
            const movedOut = Boolean(moveTo) && Number(body.moved) > 0;
            if (movedOut) {
                toast.success(
                    t("put_away_found_moved", {
                        count: String(body.moved),
                        location: typeof body.destination_display_name === "string" && body.destination_display_name
                            ? body.destination_display_name
                            : String(moveTo),
                    }),
                    { toastId: "pa-found-finished" },
                );
                // The shelf is empty again — which is what the operator came
                // for. Nothing is excluded: Cold is still at its tote step
                // for this location, Empty High still suggests it.
                setFoundState(null);
                setFoundFinishConfirmState(false);
                setScanValue("");
                return;
            }
            toast.success(
                t("put_away_found_finished", { count: String(f.added), location: f.location.display }),
                { toastId: "pa-found-finished" },
            );
            await finishFoundAndMoveOn(f.location.barcode);
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setFoundBusy(false);
        }
    };

    // Scan routing for the flow. Called from handleColdScan and from the
    // Empty High Locations branch of doSubmit BEFORE any step handling of
    // their own, so no scan can fall through to the normal flow.
    const handleFoundScan = async (f: FoundState, val: string) => {
        // A modal owns the screen — a scan now would act behind it.
        if (foundFinishConfirmRef.current) {
            setScanValue("");
            return;
        }

        if (f.stage === "location") {
            if (val !== f.location.barcode) {
                playError();
                flashStatus("error");
                toast.error(
                    t("put_away_high_wrong_location", { location: f.location.display }),
                    { toastId: TOAST_ID.highWrongLocation },
                );
                setScanValue("");
                return;
            }
            playBoxAdd();
            setFoundState({ ...f, stage: "tote" });
            setScanValue("");
            return;
        }

        if (f.stage === "tote") {
            setLoading(true);
            try {
                const w = await lookupWhereabouts(val);
                setScanValue("");
                if (!w.valid) {
                    playError();
                    flashStatus("error");
                    toast.error(t("not_a_tote"), { toastId: TOAST_ID.notATote });
                    return;
                }
                if (w.shelf || w.forecast || w.onWave) {
                    // Picked order stock. It must never be booked into
                    // storage — say where it belongs and skip this location.
                    playError();
                    flashStatus("error");
                    const home = await lookupWrongQueue(val, "put_away");
                    if (home) setWrongQueue({ toteId: val, ...home });
                    else toast.error(t("tote_not_awaiting_put_away"), { toastId: "pa-found-wrong-queue" });
                    await finishFoundAndMoveOn(f.location.barcode);
                    return;
                }
                if (w.goodsIn === true && w.toteStatus === "closed") {
                    // Still awaiting put-away — whoever left it here never
                    // scanned it in. Offer to do that now.
                    playBoxAdd();
                    setFoundState({ ...f, stage: "release", toteId: val });
                    return;
                }
                // Not awaiting put-away: already stored elsewhere, never
                // booked in, or reopened. Count what's here and book it in
                // (per Alex, 2026-09-21 — same treatment the Stock Check
                // module gives known-elsewhere stock).
                await startFoundCount({ ...f, toteId: val });
            } catch {
                setScanValue("");
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            } finally {
                setLoading(false);
            }
            return;
        }

        if (f.stage === "release") {
            // Buttons only at this step.
            setScanValue("");
            return;
        }

        if (f.stage === "destination") {
            // Any location: the BE validates it. Scanning THIS location
            // puts the units away right here.
            setScanValue("");
            await finishFound(val);
            return;
        }

        // counting
        if (val === f.location.barcode) {
            // Habit rescan of the location label — harmless.
            playBoxAdd();
            setScanValue("");
            return;
        }
        setLoading(true);
        try {
            const res = await apiFetch(`/put-away/found-stock/scan-item`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkId: f.checkId,
                    barcode: val,
                    uid: userObject.uid,
                    name: userObject.name,
                    warehouse: userObject.warehouse,
                }),
                signal: getSignal(),
            });
            if (res.status === 401) { logoutProcessor(); navigate("/login"); return; }
            const body = res.ok ? await res.json() : null;
            setScanValue("");
            if (!body?.ok) {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                return;
            }
            const outcome = body.stock_check_outcome;
            if (outcome === "unknown") {
                playError();
                flashStatus("error");
                toast.error(t("picking_missing_unknown_barcode"), { toastId: "pa-found-unknown" });
                return;
            }
            if (body.sku && (outcome === "known_not_here" || outcome === "surplus" || outcome === "already_added")) {
                // Not on the check's expected stock (or one more than it
                // expects): book it in right now — they're holding it.
                await bookFoundUnit({
                    sku: body.sku,
                    title: typeof body.title === "string" && body.title ? body.title : body.sku,
                    barcode: val,
                });
                return;
            }
            playBoxAdd();
            if (Array.isArray(body.stock_check_lines)) {
                const live = foundRef.current ?? f;
                setFoundState({ ...live, lines: body.stock_check_lines });
            }
        } catch {
            setScanValue("");
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setLoading(false);
        }
    };

    // Cold Putaway's own scan handler — kept separate from doSubmit's main
    // switch (like handleHighEmptyScan) because its three steps don't map
    // onto Phase at all; `phase` stays "awaiting-tote" for the whole flow.
    const handleColdScan = async (val: string) => {
        // "Stock already here" owns every scan while it's active.
        const activeFound = foundRef.current;
        if (activeFound) {
            await handleFoundScan(activeFound, val);
            return;
        }
        const step = coldStepRef.current;

        if (step === "anchor") {
            setLoading(true);
            try {
                const lookup = await lookupNextEmptyCold(val, coldExcludeRef.current);
                setScanValue("");
                if (!lookup.valid) {
                    playError();
                    flashStatus("error");
                    toast.error(
                        lookup.reason === "not_high" ? t("put_away_high_not_high") : t("invalid_location"),
                        { toastId: lookup.reason === "not_high" ? TOAST_ID.highNotHigh : TOAST_ID.invalidLocation },
                    );
                    return;
                }
                if (!lookup.result.next) {
                    playError();
                    flashStatus("error");
                    toast.error(t("put_away_high_none"), { toastId: TOAST_ID.invalidLocation });
                    return;
                }
                setColdAnchor(lookup.result.anchor);
                setColdSuggested(lookup.result.next);
                coldSuggestedRef.current = lookup.result.next;
                setColdStep("tote");
                coldStepRef.current = "tote";
                playSuccess();
            } catch {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            } finally {
                setLoading(false);
            }
            return;
        }

        if (step === "tote") {
            setLoading(true);
            try {
                const { valid, reason, remaining: rem, meta } = await claimTote(val, "COLD");
                setScanValue("");
                if (valid) {
                    setToteId(val);
                    setRemaining(rem ?? []);
                    itemMetaByBarcodeRef.current = meta ?? {};
                    clearPickedItem();
                    setColdStep("location");
                    coldStepRef.current = "location";
                    playBoxAdd();
                    timer.start(computeNextDurationMs());
                } else {
                    playError();
                    flashStatus("error");
                    if (reason === "not_awaiting_put_away") {
                        const home = await lookupWrongQueue(val, "put_away");
                        if (home) {
                            setWrongQueue({ toteId: val, ...home });
                            timer.pauseFor(ERROR_PAUSE_MS);
                            return;
                        }
                    }
                    const statusMsgs: Record<string, string> = {
                        status_available: t("tote_status_available"),
                        status_open: t("tote_status_open"),
                        status_stored: t("tote_status_stored"),
                        status_retired: t("tote_status_retired"),
                        not_awaiting_put_away: t("tote_not_awaiting_put_away"),
                        category_mismatch: t("put_away_cold_wrong_category"),
                    };
                    toast.error((reason && statusMsgs[reason]) || t("not_a_tote"), {
                        toastId: TOAST_ID.coldTote,
                    });
                    timer.pauseFor(ERROR_PAUSE_MS);
                }
            } catch {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            } finally {
                setLoading(false);
            }
            return;
        }

        // step === "location"
        // Rescanning the held tote abandons this putaway, same as the
        // ordinary flow's closeTote, but stays inside Cold at the "tote"
        // step (with the same suggested destination still on screen)
        // rather than exiting to plain awaiting-tote.
        if (toteIdRef.current && val === toteIdRef.current) {
            setToteId(null);
            setRemaining([]);
            itemMetaByBarcodeRef.current = {};
            clearPickedItem();
            setColdStep("tote");
            coldStepRef.current = "tote";
            timer.reset();
            playBoxAdd();
            toast.info(t("put_away_tote_closed"), { toastId: TOAST_ID.toteClosed });
            setScanValue("");
            return;
        }
        const suggested = coldSuggestedRef.current;
        if (!suggested || val !== suggested.barcode) {
            playError();
            flashStatus("error");
            toast.error(
                t("put_away_high_wrong_location", { location: suggested?.display ?? "" }),
                { toastId: TOAST_ID.highWrongLocation },
            );
            timer.pauseFor(ERROR_PAUSE_MS);
            setScanValue("");
            return;
        }
        setLoading(true);
        try {
            const result = await completeTotePutAway(val, { expectedLocation: suggested.barcode, requireEmpty: true });
            setScanValue("");
            if (result.kind === "ok") {
                stopTimerAndAward();
                playToteFinalised();
                flashStatus("final");
                resetToStart();
                await refreshColdSuggestion(suggested.barcode, "tote");
            } else if (result.kind === "occupied") {
                // Someone/something filled it since it was suggested — not
                // the operator's fault. The tote is still in hand.
                playError();
                flashStatus("error");
                toast.error(t("put_away_cold_now_occupied"), { toastId: TOAST_ID.coldOccupiedRace });
                await refreshColdSuggestion(suggested.barcode, "location");
            } else if (result.kind === "ppd_failed") {
                playError();
                flashStatus("error");
                toast.error(t("ppd_sync_failed"), { toastId: TOAST_ID.ppdFailed });
            } else {
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            }
        } catch {
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            setLoading(false);
        }
    };

    const doSubmit = async (scannedValue?: string, opts?: { silentClaim?: boolean }) => {
        const val = (scannedValue ?? scanValue).trim();
        if (!val) {
            toast.error(t("nothing_scanned"), { toastId: TOAST_ID.nothingScanned });
            return;
        }
        // The unfinished-consolidation prompt owns the screen: Resume /
        // Close tote / dismiss are the only ways on.
        if (consResumePromptRef.current) {
            setScanValue("");
            return;
        }
        // --- Tote Category mode: pure lookup, scan after scan ---
        // Handled before every put-away code path so no scan in this mode can
        // reach the claim/confirm/complete logic below.
        if (modeRef.current === "category") {
            setLoading(true);
            try {
                const lookup = await lookupToteCategory(val);
                setScanValue("");
                if (lookup.valid) {
                    // A null category is a real answer ("no category"), so it
                    // succeeds like any other — the panel says which it is.
                    //
                    // No success flash here: the category card IS the
                    // feedback, and a green tick would both be redundant and
                    // cover the answer for its 800ms lifetime. Errors still
                    // flash, since a rejected scan has nothing else to show.
                    setCategoryResult(lookup.result);
                    setCategoryScanSeq((n) => n + 1);
                    // Cancel any in-flight error flash from a previous scan so
                    // its timer can't blank the card we're about to show.
                    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
                    setScanStatus("idle");
                    // Hot/cold totes play the BF scan sheet's own clips in
                    // place of the generic success beep, so the answer is
                    // audible without looking at the screen. Resolved the
                    // same way as the card, so legacy book_in-tagged
                    // HOT/COLD rows sound right too.
                    const scannedLabel = resolveCategoryStyle(lookup.result)?.labelKey;
                    if (scannedLabel === "bf_colour_hot") playGreenHotSound();
                    else if (scannedLabel === "bf_colour_cold") playGreenColdSound();
                    else playSuccess();
                } else {
                    // Keep the previous result on screen: a stray bad scan
                    // shouldn't wipe the category the operator is still reading.
                    playError();
                    flashStatus("error");
                    toast.error(
                        lookup.reason === "never_booked_in"
                            ? t("put_away_category_never_booked_in")
                            : t("not_a_tote"),
                        {
                            toastId: lookup.reason === "never_booked_in"
                                ? "pa-cat-never-booked-in"
                                : TOAST_ID.notATote,
                        },
                    );
                }
            } catch {
                setScanValue("");
                playError();
                flashStatus("error");
                toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
            } finally {
                setLoading(false);
            }
            return;
        }

        // --- Empty High Locations mode: read-only navigator, scan after scan ---
        if (modeRef.current === "high-empty") {
            // "Stock already here" owns every scan while it's active.
            const activeHighFound = foundRef.current;
            if (activeHighFound) {
                await handleFoundScan(activeHighFound, val);
                return;
            }
            if (highViewRef.current === "consolidate") {
                await handleConsolidateScan(val);
                return;
            }
            await handleHighEmptyScan(val);
            return;
        }

        // --- Cold Putaway: layered inside Put Away mode, not a separate mode ---
        if (modeRef.current === "put-away" && coldStepRef.current) {
            await handleColdScan(val);
            return;
        }

        const pendingToteConfirm = toteConfirmRef.current;
        if (pendingToteConfirm) {
            if (val === pendingToteConfirm.locationId) {
                // Swallow the echo of the scan that opened this prompt —
                // see confirmArming.ts. Silent: no error, prompt stays.
                if (performance.now() - toteConfirmOpenedAtRef.current < CONFIRM_ARM_MS) {
                    setScanValue("");
                    return;
                }
                await confirmTotePutAway();
            } else {
                playError();
                flashStatus("error");
                toast.error(t("put_away_confirm_rescan_mismatch"), {
                    toastId: TOAST_ID.confirmMismatch,
                });
            }
            setScanValue("");
            return;
        }
        setLoading(true);

        try {
            // Rescanning the current tote closes the put-away session —
            // EXCEPT for a pallet crate at the location step. A crate's
            // barcode is also its storage location (Alex, 2026-10-12), so
            // scanning it there means "put the contents away into the
            // crate": fall through to the normal location handling below,
            // which finds location UK-0001 and opens the usual confirm.
            // Crates are abandoned with the on-screen Close button instead.
            if (toteIdRef.current && val === toteIdRef.current) {
                const crateAtLocationStep = toteTypeRef.current === "pallet"
                    && phaseRef.current === "awaiting-location";
                if (!crateAtLocationStep) {
                    closeTote();
                    return;
                }
            }

            // --- Phase 1: scan the tote ---
            if (phaseRef.current === "awaiting-tote") {
                const { valid, reason, remaining: rem, meta, type } = await claimTote(val);
                if (valid) {
                    setToteId(val);
                    // Ref first: Consolidate's "Close & Put Away" reads it straight
                    // after this call, before React has committed the state.
                    toteIdRef.current = val;
                    setToteType(type ?? "box");
                    toteTypeRef.current = type ?? "box";
                    setRemaining(rem ?? []);
                    itemMetaByBarcodeRef.current = meta ?? {};
                    clearPickedItem();
                    setPhase("awaiting-location");
                    // Handoff claim from Book In: skip the success flash/sound
                    // so we land directly on the scan item/location screen.
                    if (!opts?.silentClaim) {
                        playBoxAdd();
                    }
                    timer.start(computeNextDurationMs());
                } else {
                    playError();
                    flashStatus("error");
                    // Closed by Drop/Picking: find its real home and offer
                    // to go there instead of a dead-end toast.
                    if (reason === "not_awaiting_put_away") {
                        const home = await lookupWrongQueue(val, "put_away");
                        if (home) {
                            setWrongQueue({ toteId: val, ...home });
                            timer.pauseFor(ERROR_PAUSE_MS);
                            return;
                        }
                    }
                    // Status-specific messages so the operator knows why the
                    // tote was rejected — particularly useful for 'stored'
                    // (already put away) vs 'open' (being filled) vs
                    // 'available' (empty, nothing to put away).
                    const statusMsgs: Record<string, string> = {
                        status_available: t("tote_status_available"),
                        status_open: t("tote_status_open"),
                        status_stored: t("tote_status_stored"),
                        // One-time pallet tote already put away (migration 192).
                        status_retired: t("tote_status_retired"),
                        // 'closed' but by Picking/Drop, not book-in — the
                        // tote holds picked order stock, not goods-in work.
                        not_awaiting_put_away: t("tote_not_awaiting_put_away"),
                    };
                    const msg = (reason && statusMsgs[reason]) || t("not_a_tote");
                    const id = (reason && statusMsgs[reason])
                        ? `pa-${reason}`
                        : TOAST_ID.notATote;
                    toast.error(msg, { toastId: id });
                    timer.pauseFor(ERROR_PAUSE_MS);
                }
                return;
            }

            // --- Phase 2: in-hand, next scan is EITHER an item (item-level) OR a location (tote-level) ---
            if (phaseRef.current === "awaiting-location") {
                // Fast-path: item-level allowed AND the scan matches a
                // remaining item in this tote — no round-trip needed.
                if (allowItemLevelRef.current && remainingRef.current.includes(val)) {
                    selectPickedItem(val, itemMetaByBarcodeRef.current[val] ?? null);
                    setPhase("awaiting-item-location");
                    playBoxAdd();
                    timer.stop();
                    timer.start(computeNextDurationMs());
                    return;
                }

                // Try as a location (full-tote flow). If the scan is ALSO a
                // known item via scansheet fallback, the location check will
                // return false and we'll fall through to the item path below.
                const locResult = await checkLocation(val);
                if (locResult.valid) {
                    // Deliberately NOT stopping the timer here: it keeps
                    // running behind the prompt so a confirmed put-away can
                    // still award the remaining seconds (stopping it zeroes
                    // timer.state.active and stopTimerAndAward becomes a
                    // no-op). Cancel restarts it fresh.
                    const pending: ToteConfirm = {
                        locationId: val,
                        displayName: locResult.displayName,
                        sellable: locResult.sellable,
                        units: remainingRef.current.length,
                    };
                    toteConfirmRef.current = pending;
                    toteConfirmOpenedAtRef.current = performance.now();
                    setToteConfirm(pending);
                    return;
                }

                // Not a location. If item-level is on, ask the server whether
                // this is a scansheet item we can still place (out-of-tote).
                let itemReason: string | undefined;
                if (allowItemLevelRef.current) {
                    const itemCheck = await checkItem(val);
                    if (itemCheck.source === "scansheet" || itemCheck.source === "tote") {
                        selectPickedItem(val, itemCheck);
                        setPhase("awaiting-item-location");
                        playBoxAdd();
                        timer.stop();
                        timer.start(computeNextDurationMs());
                        return;
                    }
                    itemReason = itemCheck.reason;
                }

                // Neither location nor pickable item.
                playError();
                flashStatus("error");
                const msg = itemReason === "already_put_away"
                    ? t("put_away_item_already_done")
                    : allowItemLevelRef.current
                        ? t("scan_not_recognised")
                        : t("invalid_location");
                toast.error(msg, {
                    toastId: allowItemLevelRef.current ? TOAST_ID.unknownScan : TOAST_ID.invalidLocation,
                });
                timer.pauseFor(ERROR_PAUSE_MS);
                return;
            }

            // --- Phase 3: have a picked item, need a location ---
            if (phaseRef.current === "awaiting-item-location") {
                const locResult = await checkLocation(val);
                if (!locResult.valid) {
                    playError();
                    flashStatus("error");
                    toast.error(t("invalid_location"), { toastId: TOAST_ID.invalidLocation });
                    timer.pauseFor(ERROR_PAUSE_MS);
                    return;
                }
                if (locResult.sellable === "unsellable") {
                    timer.stop();
                    setUnsellableConfirm({
                        mode: "item",
                        locationId: val,
                        displayName: locResult.displayName,
                        itemBarcode: pickedItemRef.current!,
                    });
                    return;
                }
                const result = await completeItemPutAway(val, pickedItemRef.current!);
                if (!result || !result.success) {
                    playError();
                    flashStatus("error");
                    toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
                    return;
                }
                setRemaining(result.remaining);
                if (pickedItemMetaRef.current) setLastPutAway(pickedItemMetaRef.current);
                clearPickedItem();
                if (result.toteEmpty) {
                    stopTimerAndAward();
                    playToteFinalised();
                    flashStatus("final");
                    resetToStart();
                } else {
                    playSuccess();
                    setPhase("awaiting-location");
                    timer.stop();
                    timer.start(computeNextDurationMs());
                }
            }
        } catch (err) {
            if ((err as any)?.name === "AbortError" || !mountedRef.current) return;
            playError();
            flashStatus("error");
            toast.error(t("server_error"), { toastId: TOAST_ID.serverError });
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                setScanValue("");
            }
        }
    };

    // Hand-off from Book In's "Complete & Putaway": the tote was just closed
    // there and its ID passed via navigation state. Auto-claim it (same path a
    // manual tote scan takes) so the operator lands straight on the scan
    // item/location step instead of re-scanning the tote they're already
    // holding. Fires once; the history state is cleared so a refresh/back
    // doesn't re-claim. Safe because Book In only navigates here after the
    // close is confirmed, so the tote is genuinely 'closed' and claimable.
    const autoClaimRef = useRef(false);
    useEffect(() => {
        const handoffTote = (routerLocation.state as { autoClaimTote?: string } | null)?.autoClaimTote;
        if (!handoffTote || autoClaimRef.current) return;
        autoClaimRef.current = true;
        navigate(routerLocation.pathname, { replace: true, state: null });
        (async () => {
            try {
                await doSubmit(handoffTote, { silentClaim: true });
            } finally {
                // Reveal the scan UI once the claim settles — on success the
                // scan item/location step; on failure the normal scan-tote
                // command so the operator can claim manually.
                if (mountedRef.current) setHandoffClaiming(false);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pembroke: an operator who left the page mid-Consolidate is asked
    // (Shelf Drop's unfinished-session prompt) whether to resume or close
    // their tote — unless Book In is handing a tote over, which wins.
    // Fires once.
    const consResumeCheckedRef = useRef(false);
    useEffect(() => {
        if (!isPembroke || consResumeCheckedRef.current) return;
        consResumeCheckedRef.current = true;
        if ((routerLocation.state as { autoClaimTote?: string } | null)?.autoClaimTote) return;
        (async () => {
            const active = await fetchConsolidateActive();
            if (!active || !mountedRef.current) return;
            setConsResumeState(active);
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Command text reflects which phase we're in. When item-level is allowed
    // and we're in-hand, encourage item scans — users can still scan a
    // location and the full tote will be moved.
    let commandText = t("scan_tote");
    if (mode === "category") {
        // Category mode only ever wants a tote, at every scan.
        commandText = t("scan_tote");
    } else if (found?.stage === "location") {
        // "Stock already here" — reachable from Cold and from Empty High
        // Locations, so it outranks both modes' own prompts.
        commandText = t("scan_location");
    } else if (found?.stage === "tote") {
        commandText = t("scan_tote");
    } else if (found?.stage === "release") {
        commandText = t("put_away_found_release_cmd");
    } else if (found?.stage === "counting") {
        commandText = t("put_away_found_count_cmd");
    } else if (found?.stage === "destination") {
        commandText = t("scan_location");
    } else if (mode === "high-empty" && highView === "consolidate") {
        commandText = cons.step === "tote" ? t("scan_tote")
            : cons.step === "anchor" ? t("scan_location")
            : cons.step === "location" ? t("put_away_high_go_to")
            : t("scan_item");
    } else if (mode === "high-empty") {
        // Anchor scan, or the arrival scan at the suggested location.
        commandText = highEmpty?.next ? t("put_away_high_go_to") : t("scan_location");
    } else if (coldStep === "anchor") {
        commandText = t("scan_location");
    } else if (coldStep === "tote") {
        commandText = t("scan_tote");
    } else if (coldStep === "location") {
        commandText = t("put_away_high_go_to");
    } else if (phase === "awaiting-location") {
        commandText = allowItemLevel ? t("scan_item_or_location") : t("scan_location");
    } else if (phase === "awaiting-item-location") {
        commandText = t("scan_location");
    }
    const commandWords = commandText.toUpperCase().split(/[\s-]+/).filter(Boolean);

    // Sub-hint shown in muted caps under the command — same treatment
    // book-in and scansheet use, so the put-away flow shares their
    // visual language. Phase-specific so the hint always tells the
    // operator what kind of barcode resolves at the current step.
    const commandSubText = mode === "category"
        ? t("put_away_category_sub")
        : found?.stage === "location"
            ? t("put_away_found_sub_location")
            : found?.stage === "tote"
                ? t("put_away_found_sub_tote")
                : found?.stage === "release"
                    ? t("put_away_found_sub_release")
                    : found?.stage === "counting"
                        ? t("put_away_found_sub_count")
                        : found?.stage === "destination"
                            ? t("put_away_found_sub_dest", { count: String(foundCountedUnits(found)) })
        : mode === "high-empty" && highView === "consolidate"
            ? (cons.step === "tote"
                ? t("put_away_consolidate_sub_tote")
                : cons.step === "anchor"
                    ? t("put_away_consolidate_sub_anchor")
                    : cons.step === "location"
                        ? t("put_away_consolidate_sub_arrive")
                        : t("put_away_consolidate_sub_items"))
        : mode === "high-empty"
            ? (highEmpty === null
                ? t("put_away_high_sub_anchor")
                : highEmpty.next
                    ? t("put_away_high_sub_arrive")
                    : t("put_away_high_sub_none"))
        : coldStep === "anchor"
            ? t("put_away_cold_sub_anchor")
            : coldStep === "tote"
                ? t("put_away_cold_sub_tote")
                : coldStep === "location"
                    ? t("put_away_cold_sub_location")
        : phase === "awaiting-tote"
            ? t("put_away_sub_tote")
            : phase === "awaiting-item-location"
                ? ""
                : toteType === "pallet"
                    ? t("put_away_crate_sub", { tote: toteId ?? "" })
                    : allowItemLevel
                        ? t("put_away_sub_item_or_loc")
                        : t("put_away_sub_loc");

    // Switching modes is only offered when nothing is in hand — see the
    // guard in switchMode. A claimed tote must be finished or closed first.
    // Also locked for the whole Cold Putaway session (not just once a tote
    // is claimed) — exitCold, not the mode pills, is the way out while a
    // walk is in progress.
    // A Consolidate tote in hand locks the pills the same way — Tote full
    // (not a pill) is the way out.
    const modeLocked = (mode === "put-away" && (toteId !== null || coldStep !== null))
        || (mode === "high-empty" && (cons.toteId !== null || found !== null));
    const switchMode = (next: Mode) => {
        if (next === mode) return;
        // Never abandon an in-hand tote by switching tabs.
        if (modeLocked) return;
        setMode(next);
        modeRef.current = next;
        setCategoryResult(null);
        highEmptyRef.current = null;
        highVisitedRef.current = [];
        setHighEmpty(null);
        highMissingRef.current = null;
        setHighMissingConfirm(null);
        setHighViewState("empty");
        resetConsolidate();
        // Also drops an Empty High Locations "Stock already here" report
        // (the found* state is shared with Cold).
        exitCold();
        setScanValue("");
        setScanStatus("idle");
        resetToStart();
    };

    // Consolidate items-step counter: units scanned onto the tote out of
    // the units this location had when they arrived (plus any booked in
    // on the spot). "done" = everything the data knew about is on the tote.
    const consCounter = (() => {
        const rows = cons.target?.contents ?? [];
        const total = rows.reduce((a, r) => a + r.startQty, 0);
        const scanned = rows.reduce((a, r) => a + Math.max(r.startQty - r.qty, 0), 0);
        return { total, scanned, done: total > 0 && scanned >= total };
    })();

    // Swatch/label for the current result — see resolveCategoryStyle for
    // the source-keying + legacy-row fallback rules.
    const categoryStyle = resolveCategoryStyle(categoryResult);

    // The hot/cold faces the BF scan sheet shows on an item scan. Keyed on
    // the RESOLVED label, not source:category, so legacy book_in:HOT/COLD
    // rows get theirs too.
    const categoryVerdictImg =
        categoryStyle?.labelKey === "bf_colour_hot" ? greenHotImg
        : categoryStyle?.labelKey === "bf_colour_cold" ? greenColdImg
        : null;

    // Map put-away's status enum onto ScanStatusIcons' four states.
    // "final" (last item put away / tote complete) reuses the
    // "complete" yellow icon, so put-away inherits the same tick
    // language book-in uses.
    const iconStatus: "idle" | "success" | "error" | "complete" =
        scanStatus === "final" ? "complete" : scanStatus;

    return (
        <div
            className="scan-panel book-in-scan-panel put-away-scan-panel"
            onMouseDown={preventScanPageButtonFocus}
            onClick={blurClickedScanPageButton}
        >
                <ScanFlashOverlay active={iconStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{t("put_away")}</h1>
                <ScoreDisplay total={score.total} />
                {/* Icon-only mode toggle, same pills and same slot in the
                    title bar as Shelf Drop's Pick/Put-Away toggle. Hidden
                    once a tote is in hand so switching can't orphan an
                    active put-away. */}
                {!modeLocked && (
                    <div className="picking-tier-toggle" role="radiogroup" aria-label={t("put_away_mode_toggle")}>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "put-away"}
                            className={`picking-tier-pill${mode === "put-away" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => switchMode("put-away")}
                            title={t("put_away")}
                            aria-label={t("put_away")}
                        >
                            <PackageOpen size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "category"}
                            className={`picking-tier-pill${mode === "category" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => switchMode("category")}
                            title={t("put_away_category_mode")}
                            aria-label={t("put_away_category_mode")}
                        >
                            <Tags size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={mode === "high-empty"}
                            className={`picking-tier-pill${mode === "high-empty" ? " picking-tier-pill--active" : ""}`}
                            onClick={() => switchMode("high-empty")}
                            title={t("put_away_high_mode")}
                            aria-label={t("put_away_high_mode")}
                        >
                            <ArrowUpToLine size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                    </div>
                )}
                <button className="page-back-btn" onClick={() => navigate("/")}>
                    <ArrowLeft size={20} />
                </button>
            </div>

            <ProgressBar progress={timer.state.progress} visible={timer.state.active} />

            {mode === "put-away" && toteId && (
                <div className="book-in-stats-bar">
                    <span className="book-in-stat">
                        <Package size={16} />
                        <strong>{t("tote_label", { tote: toteId })}</strong>
                    </span>
                    <span className="book-in-stat book-in-stat--count">
                        {t("items_count", { count: String(remaining.length) })}
                    </span>
                </div>
            )}
            {mode === "high-empty" && highView === "consolidate" && cons.toteId && (
                <div className="book-in-stats-bar" data-testid="put-away-cons-stats">
                    <span className="book-in-stat">
                        <Package size={16} />
                        <strong>{t("tote_label", { tote: cons.toteId })}</strong>
                    </span>
                    <span className="book-in-stat book-in-stat--count">
                        {t("put_away_consolidate_on_tote", { count: String(cons.movedUnits) })}
                    </span>
                </div>
            )}

            <div className="scan-centre">
                {/* Empty ↔ Consolidate — Pembroke's second view on this pill.
                    Hidden while a Consolidate tote is in hand (Tote full is
                    the way out), like the mode pills above. */}
                {isPembroke && mode === "high-empty" && !cons.toteId && !found && (
                    <div
                        className="put-away-high-view-toggle"
                        role="radiogroup"
                        aria-label={t("put_away_high_view_toggle")}
                        data-testid="put-away-high-view-toggle"
                    >
                        <button
                            type="button"
                            role="radio"
                            aria-checked={highView === "empty"}
                            className={`put-away-high-view-pill${highView === "empty" ? " put-away-high-view-pill--active" : ""}`}
                            onClick={() => switchHighView("empty")}
                        >
                            {t("put_away_high_view_empty")}
                        </button>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={highView === "consolidate"}
                            className={`put-away-high-view-pill${highView === "consolidate" ? " put-away-high-view-pill--active" : ""}`}
                            onClick={() => switchHighView("consolidate")}
                        >
                            {t("put_away_high_view_consolidate")}
                        </button>
                    </div>
                )}
                <div className="scan-command-wrap">
                    {/* Empty-per-zone popup opener — same ⓘ Shelf Drop puts
                        above its scan glyph. Only in this mode. */}
                    {mode === "high-empty" && (
                        <button
                            type="button"
                            className="drop-aisles-btn"
                            onClick={openHighZones}
                            title={highView === "consolidate" ? t("put_away_consolidate_zones_title") : t("put_away_high_zones_title")}
                            aria-label={highView === "consolidate" ? t("put_away_consolidate_zones_title") : t("put_away_high_zones_title")}
                        >
                            <Info size={22} />
                        </button>
                    )}
                    <ScanStatusIcons status={iconStatus === "error" ? "idle" : iconStatus} />
                    <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                        {!handoffClaiming && commandWords.map((word, i) => (
                            <FitWord key={i}>{word}</FitWord>
                        ))}
                    </div>
                    {handoffClaiming ? null : commandSubText ? (
                        <div className={`scan-command-sub ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                            {commandSubText}
                        </div>
                    ) : null}

                    {/* Pallet crate in hand: rescanning the crate puts its
                        contents away INTO the crate (its barcode is also the
                        location), so the rescan-to-abandon gesture is gone.
                        This button is the abandon gesture for crates. */}
                    {phase === "awaiting-location" && toteId && toteType === "pallet"
                        && scanStatus === "idle" && !toteConfirm && (
                        <button
                            type="button"
                            className="book-in-command-action-btn put-away-crate-close-btn"
                            onClick={closeTote}
                        >
                            {t("put_away_crate_close", { tote: toteId })}
                        </button>
                    )}

                    {/* Cold Putaway entry point — Pembroke only, and only
                        before anything is claimed/started. The BF scan
                        sheet's own cold face (the same image the Tote
                        Category card shows for a COLD tote), large and
                        centred, so it reads as the one thing to tap. */}
                    {isPembroke && mode === "put-away" && phase === "awaiting-tote"
                        && !toteId && coldStep === null && scanStatus === "idle" && (
                        <button
                            type="button"
                            className="put-away-cold-start"
                            onClick={startCold}
                        >
                            <img src={greenColdImg} alt="" aria-hidden="true" />
                            <span>{t("put_away_temp_cold")}</span>
                        </button>
                    )}

                    {/* Cold Putaway suggestion — same visual language as
                        Empty High Locations' card, since it's the same
                        underlying lookup. Report actions only make sense
                        before the tote is claimed (that's when the operator
                        is stood there checking the spot is actually empty). */}
                    {coldStep && coldStep !== "anchor" && coldSuggested && !found && scanStatus === "idle" && (
                        <div className="put-away-high-card put-away-cold-card" data-testid="put-away-cold-card">
                            <div className="put-away-high-location">
                                {coldSuggested.display}
                            </div>
                            {/* Same buttons, same styling, same place as Empty
                                High Locations' own "Location doesn't exist"
                                (per Alex, 2026-09-21). Both go through a
                                confirm or a scan gate before anything is
                                written, so a stray tap is recoverable. */}
                            {coldStep === "tote" && (
                                <div className="put-away-cold-report-row">
                                    <button
                                        type="button"
                                        className="put-away-high-missing-btn"
                                        onClick={openColdMissing}
                                    >
                                        {t("put_away_high_missing")}
                                    </button>
                                    <button
                                        type="button"
                                        className="put-away-high-missing-btn"
                                        onClick={openColdOccupied}
                                    >
                                        {t("put_away_cold_occupied_btn")}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* "Stock already here" — one step on screen at a time.
                        Shared by Cold Putaway and Empty High Locations. */}
                    {found && scanStatus === "idle" && (
                        <div
                            className="put-away-high-card put-away-found-card"
                            data-testid="put-away-found-card"
                            data-stage={found.stage}
                        >
                            {found.stage !== "counting" && found.stage !== "destination" && (
                                <div className="put-away-high-location">
                                    {found.location.display}
                                </div>
                            )}
                            {found.stage === "destination" && (
                                <div className="put-away-found-tote">
                                    <Package size={18} aria-hidden="true" />
                                    <span>{t("items_count", { count: String(foundCountedUnits(found)) })}</span>
                                </div>
                            )}
                            {found.stage === "release" && (
                                <>
                                    <div className="put-away-found-tote">
                                        <Package size={18} aria-hidden="true" />
                                        <span>{t("tote_label", { tote: found.toteId ?? "" })}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-lg"
                                        onClick={releaseFoundTote}
                                        disabled={foundBusy}
                                    >
                                        {foundBusy ? t("submitting") : t("put_away_found_release")}
                                    </button>
                                </>
                            )}
                            {found.stage === "counting" && (
                                <div className="picking-missing-screen put-away-found-count">
                                    <MissingItemCountList
                                        targetSku=""
                                        location={found.location.barcode}
                                        locationDisplayName={found.location.display}
                                        expected={[]}
                                        counts={{}}
                                        stockCheckLines={found.lines}
                                    />
                                    {found.lines.length === 0 && (
                                        <p className="put-away-found-empty">{t("put_away_found_count_empty")}</p>
                                    )}
                                    <div className="picking-missing-actions">
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={startFoundFinish}
                                            disabled={foundBusy}
                                        >
                                            {t("put_away_found_finish")}
                                        </button>
                                    </div>
                                </div>
                            )}
                            {found.stage !== "counting" && found.stage !== "destination" && (
                                <button
                                    type="button"
                                    className="book-in-command-action-btn put-away-cold-cancel-btn"
                                    onClick={cancelFound}
                                    disabled={foundBusy}
                                >
                                    {t("cancel")}
                                </button>
                            )}
                        </div>
                    )}
                    {coldStep && coldStep !== "location" && !toteId && !found && (
                        <button
                            type="button"
                            className="book-in-command-action-btn put-away-cold-cancel-btn"
                            onClick={exitCold}
                        >
                            {t("put_away_cold_cancel")}
                        </button>
                    )}

                    {/* Category result — the whole point of this mode. Stays
                        on screen until the next successful scan replaces it. */}
                    {mode === "category" && categoryResult && scanStatus === "idle" && (
                        // Keyed on the scan sequence so EVERY successful scan
                        // remounts the block and replays the flash-to-solid
                        // animation — a same-colour tote must still visibly
                        // register as a new answer. A Fragment (not a div)
                        // keeps the card and the face DIRECT children of
                        // .scan-command-wrap, whose flex centring is what
                        // centres the card.
                        <Fragment key={categoryScanSeq}>
                            <div
                                className="put-away-category-card"
                                style={{
                                    background: categoryStyle?.swatch ?? NO_CATEGORY_SWATCH.swatch,
                                    color: categoryStyle?.text ?? NO_CATEGORY_SWATCH.text,
                                }}
                            >
                                <div className="put-away-category-name">
                                    {(categoryResult.supplierName || categoryResult.poNumber)
                                        ? t("po_chip", {
                                            supplier: categoryResult.supplierName ?? "—",
                                            po: categoryResult.poNumber ?? "—",
                                        })
                                        : categoryStyle
                                        ? t(categoryStyle.labelKey)
                                        : categoryResult.category
                                            // A value the FE doesn't have a swatch
                                            // for (e.g. a category added on the BE
                                            // later) still shows its raw name
                                            // rather than claiming "no category".
                                            ? categoryResult.category
                                            : t("put_away_category_none")}
                                </div>
                                <div className="put-away-category-tote">
                                    {categoryResult.toteId}
                                </div>
                                <div className="put-away-category-meta">
                                    {t("items_count", { count: String(categoryResult.unitsTotal) })}
                                </div>
                            </div>
                            {categoryVerdictImg && (
                                <img
                                    className="put-away-category-face"
                                    data-testid="put-away-category-face"
                                    src={categoryVerdictImg}
                                    alt=""
                                    aria-hidden="true"
                                />
                            )}
                        </Fragment>
                    )}

                    {/* Empty High Locations — the destination in the picker
                        hero's plain black aisle-bay-level style (per Alex:
                        no colour panel, no count). Keyed on the scan sequence
                        so every answer remounts and re-animates. */}
                    {mode === "high-empty" && highView === "empty" && highEmpty && !found && scanStatus === "idle" && (
                        <div
                            key={highEmptySeq}
                            className="put-away-high-card"
                            data-testid="put-away-high-card"
                        >
                            {highEmpty.next ? (
                                <div className="put-away-high-location">
                                    {highEmpty.next.display}
                                </div>
                            ) : (
                                <>
                                    <div className="put-away-high-none">
                                        {t("put_away_high_none")}
                                    </div>
                                    <div className="put-away-high-from">
                                        {t("put_away_high_from", { location: highEmpty.anchor.display })}
                                    </div>
                                </>
                            )}
                            <button
                                type="button"
                                className="book-in-command-action-btn put-away-high-change-btn"
                                onClick={changeHighLocation}
                            >
                                {t("put_away_high_change")}
                            </button>
                            {/* The two report actions, side by side — the
                                same pair, same styling, as the Cold card
                                (per Alex, 2026-09-23). Both go through a
                                confirm or a scan gate before anything is
                                written, so a stray tap is recoverable. */}
                            {highEmpty.next && (
                                <div className="put-away-cold-report-row">
                                    <button
                                        type="button"
                                        className="put-away-high-missing-btn"
                                        onClick={openHighMissing}
                                    >
                                        {t("put_away_high_missing")}
                                    </button>
                                    <button
                                        type="button"
                                        className="put-away-high-missing-btn"
                                        onClick={openHighOccupied}
                                    >
                                        {t("put_away_cold_occupied_btn")}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Consolidate — the suggested location in the same
                        big plain style, the pin above it as the "Skip
                        location" control (Picking's gesture), and what is
                        on the shelf as Move Stock's rows. Keyed on the
                        suggestion sequence so every new answer re-animates. */}
                    {mode === "high-empty" && highView === "consolidate" && scanStatus === "idle"
                        && cons.target && (cons.step === "location" || cons.step === "items") && (
                        <div
                            key={consSeq}
                            className="put-away-high-card put-away-cons-card"
                            data-testid="put-away-cons-card"
                            data-step={cons.step}
                        >
                            <button
                                type="button"
                                className="put-away-cons-pin"
                                onClick={openConsChange}
                                title={t("put_away_high_change")}
                                aria-label={t("put_away_high_change")}
                            >
                                <MapPin size={40} strokeWidth={1.75} aria-hidden="true" />
                            </button>
                            {/* The location alone on the way there; once
                                arrived, what the data says is here (FNSKU +
                                title, per Alex 2026-09-23) so the operator
                                knows what they're looking for. Every unit
                                must still be scanned onto the tote — the
                                rows tick off as scans land; "Not here"
                                writes off what the shelf doesn't hold. */}
                            <div className="put-away-high-location">
                                {cons.target.display}
                            </div>
                            {cons.step === "items" && cons.target.contents.length > 0 && (
                                <div className="stock-check-list put-away-cons-list" data-testid="put-away-cons-list">
                                    {cons.target.contents.map((row) => {
                                        const scanned = Math.max(row.startQty - row.qty, 0);
                                        const done = row.startQty > 0 && row.qty === 0;
                                        return (
                                            <div
                                                key={row.sku}
                                                className={`stock-check-row put-away-cons-row${done ? " stock-check-row--done" : ""}`}
                                                data-testid="put-away-cons-row"
                                                data-sku={row.sku}
                                            >
                                                <div className="stock-check-row-tick">
                                                    {done ? <Check size={18} /> : <span className="stock-check-row-dot" />}
                                                </div>
                                                <div className="stock-check-row-main">
                                                    <span className="stock-check-row-title">
                                                        {row.title.length > 70 ? `${row.title.slice(0, 70)}…` : row.title}
                                                    </span>
                                                    <span className="stock-check-row-identity">
                                                        <span className="stock-check-row-sku">{row.sku}</span>
                                                    </span>
                                                </div>
                                                <div className="stock-check-row-count">
                                                    {scanned}<span className="stock-check-row-count-sep">/</span>{row.startQty}
                                                </div>
                                                {row.qty > 0 && (
                                                    <button
                                                        type="button"
                                                        className="put-away-cons-not-here-btn"
                                                        onClick={() => openConsWriteOff(row)}
                                                        disabled={consBusy}
                                                        aria-label={t("put_away_consolidate_not_here_btn_aria", { title: row.title })}
                                                    >
                                                        <PackageX size={14} aria-hidden="true" />
                                                        {t("put_away_consolidate_not_here_btn")}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Scanned / to scan — the one tally on this step
                                (per Alex, 2026-09-22). Goes green when the
                                data's units are all on the tote; a further
                                scan (an unrecorded unit) books in and adds
                                to both numbers. */}
                            {cons.step === "items" && (
                                <div
                                    className={`put-away-cons-counter${consCounter.done ? " put-away-cons-counter--done" : ""}`}
                                    data-testid="put-away-cons-counter"
                                >
                                    {consCounter.scanned} / {consCounter.total}
                                </div>
                            )}
                        </div>
                    )}
                    {/* Actions under the card: "Next location" (items step —
                        done here, carry on; green once the counter is
                        complete) and "Tote full" (the units here are too
                        bulky to move — leave the location). */}
                    {mode === "high-empty" && highView === "consolidate" && scanStatus === "idle" && cons.toteId
                        && cons.target && (cons.step === "location" || cons.step === "items") && (
                        <div className="put-away-cons-actions">
                            {cons.step === "items" && (
                                <button
                                    type="button"
                                    className={`btn btn-lg put-away-cons-next-btn ${consCounter.done ? "btn-success" : "btn-secondary"}`}
                                    onClick={consolidateNextLocation}
                                    disabled={consBusy}
                                    data-testid="put-away-cons-next"
                                >
                                    {t("put_away_consolidate_next_location")}
                                </button>
                            )}
                            <button
                                type="button"
                                className="book-in-command-action-btn put-away-cons-full-btn"
                                onClick={openConsFull}
                                disabled={consBusy}
                                data-testid="put-away-cons-full"
                            >
                                <PackageCheck size={18} aria-hidden="true" />
                                {t("put_away_consolidate_tote_full")}
                            </button>
                        </div>
                    )}
                    {phase === "awaiting-item-location" && pickedItemMeta && scanStatus === "idle" && (
                        <ScannedItemCard
                            title={pickedItemMeta.title}
                            barcode={pickedItemMeta.barcode}
                            thumbnailUrl={pickedItemMeta.thumbnailUrl}
                            imageUrl={pickedItemMeta.imageUrl}
                        />
                    )}
                </div>
            </div>

            <HiddenScanInput
                value={scanValue}
                onChange={setScanValue}
                onSubmit={doSubmit}
                disabled={loading}
                onKeyboardToggle={(open) => {
                    const was = keyboardOpenRef.current;
                    keyboardOpenRef.current = open;
                    if (timer.state.active && was !== open) {
                        timer.extend(open ? KEYBOARD_BONUS_MS : -KEYBOARD_BONUS_MS);
                    }
                }}
            />

            <LastPutAwayBanner item={lastPutAway} />

            {wrongQueue && (
                <WrongQueueModal
                    toteId={wrongQueue.toteId}
                    queue={wrongQueue.queue}
                    info={wrongQueue.info}
                    onGo={(path) => { setWrongQueue(null); navigate(path); }}
                    onClose={() => setWrongQueue(null)}
                />
            )}
            {consChangeOpen && cons.target && (
                // "Change location": the destination is confirmed only by
                // scanning it (Picking's skip prompt); Cancel leaves the
                // suggestion untouched. Nothing is recorded.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsChange}>
                    <div
                        className="modal-card book-in-reopen-modal"
                        data-testid="put-away-cons-change-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_high_change")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsChange}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <MapPin size={32} />
                                </div>
                                <div className="book-in-close-card-id">{cons.target.display}</div>
                            </div>
                            <p className="book-in-close-hint">{t("put_away_consolidate_change_body")}</p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsChange}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consFullOpen && cons.target && (
                // "Tote full": leave this bulky location. A wrong tap would
                // hide the location until something is picked from it, so
                // it is confirmed.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsFull}>
                    <div
                        className="modal-card book-in-close-modal"
                        data-testid="put-away-cons-full-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_tote_full_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsFull}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageCheck size={32} />
                                </div>
                                <div className="book-in-close-card-id">{cons.target.display}</div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_tote_full_body", { location: cons.target.display })}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsFull}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={confirmConsFull}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_tote_full_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consResume && (
                // Unfinished consolidation on entry (Shelf Drop's resume modal).
                <div className="modal-overlay book-in-modal-overlay" onClick={dismissConsolidateResume}>
                    <div
                        className="modal-card book-in-reopen-modal"
                        data-testid="put-away-cons-resume-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_resume_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={dismissConsolidateResume}
                                disabled={consBusy}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consResume.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{consResume.movedUnits}</span>
                                    <span className="book-in-close-card-count-label">
                                        {consResume.movedUnits === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_resume_body", {
                                    tote: consResume.toteId,
                                    units: String(consResume.movedUnits),
                                })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-secondary btn-lg"
                                    onClick={closeConsolidateFromResume}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_resume_close")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={resumeConsolidate}
                                    disabled={consBusy}
                                >
                                    {t("put_away_consolidate_resume_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consBookIn && cons.target && (
                // Not recorded here: book one in and move it, or not this one.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsBookIn}>
                    <div
                        className="modal-card book-in-reopen-modal put-away-cons-confirm"
                        data-testid="put-away-cons-bookin-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_book_in_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsBookIn}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <AlertTriangle size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consBookIn.title}</div>
                                <div className="put-away-cons-bookin-meta">
                                    <span>{consBookIn.sku}</span>
                                    <span>{consBookIn.barcode}</span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_book_in_body", {
                                    title: consBookIn.title,
                                    location: cons.target.display,
                                })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsBookIn}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={confirmConsBookIn}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_book_in_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consWriteOff && cons.target && (
                // "Not here": write the recorded unit(s) off, or cancel.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsWriteOff}>
                    <div
                        className="modal-card book-in-reopen-modal put-away-cons-confirm"
                        data-testid="put-away-cons-writeoff-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_write_off_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsWriteOff}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageX size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consWriteOff.title}</div>
                                <div className="put-away-cons-bookin-meta">
                                    <span>{consWriteOff.sku}</span>
                                    {consWriteOff.barcode && <span>{consWriteOff.barcode}</span>}
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_write_off_body", {
                                    qty: String(consWriteOff.qty),
                                    title: consWriteOff.title,
                                    location: cons.target.display,
                                })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsWriteOff}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={confirmConsWriteOff}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_write_off_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consClose && (
                // Close Tote (Shelf Drop): rescan the same tote or tap to confirm.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsClose}>
                    <div
                        className="modal-card book-in-reopen-modal"
                        data-testid="put-away-cons-close-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_close_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsClose}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consClose.toteId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{consClose.units}</span>
                                    <span className="book-in-close-card-count-label">
                                        {consClose.units === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">{t("put_away_consolidate_close_body")}</p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsClose}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-secondary btn-lg"
                                    onClick={() => confirmConsClose(false)}
                                    disabled={consBusy}
                                    data-testid="put-away-cons-close-queue"
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_close_confirm")}
                                </button>
                                {consClose.units > 0 && (
                                    <button
                                        className="btn btn-success btn-lg"
                                        onClick={() => confirmConsClose(true)}
                                        disabled={consBusy}
                                        data-testid="put-away-cons-close-put-away"
                                    >
                                        {consBusy ? t("submitting") : t("put_away_consolidate_close_put_away")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consSwap && cons.toteId && (
                // Change Tote (Shelf Drop): rescan the new tote or tap Confirm.
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsSwap}>
                    <div
                        className="modal-card book-in-reopen-modal"
                        data-testid="put-away-cons-swap-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_swap_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsSwap}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <PackageOpen size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consSwap.toteId}</div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_swap_body", { tote: consSwap.toteId, current: cons.toteId })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsSwap}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={confirmConsSwap}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_swap_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {consReopen && (
                // A tote this screen closed earlier, scanned again — carry
                // on filling it rather than rejecting it as "already closed".
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelConsReopen}>
                    <div
                        className="modal-card book-in-reopen-modal"
                        data-testid="put-away-cons-reopen-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_consolidate_reopen_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelConsReopen}
                                disabled={consBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <Package size={32} />
                                </div>
                                <div className="book-in-close-card-id">{consReopen.tote}</div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_consolidate_reopen_body", {
                                    tote: consReopen.tote,
                                    units: String(consReopen.units),
                                })}
                            </p>
                            <div className="modal-confirm-buttons book-in-reopen-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelConsReopen}
                                    disabled={consBusy}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-primary btn-lg"
                                    onClick={confirmConsReopen}
                                    disabled={consBusy}
                                >
                                    {consBusy ? t("submitting") : t("put_away_consolidate_reopen_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {highZonesOpen && (
                // Empty high locations per zone. Rows arrive BE-ordered
                // (HIGH H, HIGH I-J … HIGH AA, HIGH I folded into I-J) and use the
                // same rule as the navigator, so the total is exactly what
                // the mode can still suggest.
                <div className="modal-overlay book-in-modal-overlay" onClick={() => setHighZonesOpen(false)}>
                    <div
                        className="modal-card book-in-reopen-modal drop-aisles-modal"
                        data-testid="put-away-high-zones-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{highView === "consolidate" ? t("put_away_consolidate_zones_title") : t("put_away_high_zones_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setHighZonesOpen(false)}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            {highZoneRows === null ? (
                                <FlowLoader />
                            ) : highZonesFailed ? (
                                <p className="drop-aisles-empty">{t("server_error")}</p>
                            ) : highZoneRows.length === 0 ? (
                                <p className="drop-aisles-empty">
                                    {highView === "consolidate" ? t("put_away_consolidate_none") : t("put_away_high_none")}
                                </p>
                            ) : (
                                <>
                                    <p className="drop-aisles-hint">
                                        {highView === "consolidate" ? t("put_away_consolidate_zones_hint") : t("put_away_high_zones_hint")}
                                    </p>
                                    <div className="drop-aisles-list">
                                        {highZoneRows.map((r) => (
                                            <div className="drop-aisles-row" key={r.label}>
                                                <span className="drop-aisles-aisle">{r.label}</span>
                                                <span className="drop-aisles-units">{r.empty}</span>
                                            </div>
                                        ))}
                                        <div className="drop-aisles-row drop-aisles-row--total">
                                            <span className="drop-aisles-aisle">{t("drop_aisles_total")}</span>
                                            <span className="drop-aisles-units">
                                                {highZoneRows.reduce((s, r) => s + r.empty, 0)}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {toteConfirm && (
                <div className="modal-overlay book-in-modal-overlay" onClick={cancelToteConfirm}>
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("put_away_confirm_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelToteConfirm}
                                disabled={confirmingTote}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <MapPin size={32} />
                                </div>
                                <div className="book-in-close-card-id">{toteConfirm.displayName || toteConfirm.locationId}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{toteConfirm.units}</span>
                                    <span className="book-in-close-card-count-label">
                                        {toteConfirm.units === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_confirm_hint", {
                                    location: toteConfirm.displayName || toteConfirm.locationId,
                                    units: toteConfirm.units,
                                })}
                            </p>
                            {toteConfirm.sellable === "unsellable" && (
                                <p className="book-in-close-hint book-in-close-hint--danger">
                                    {t("unsellable_location_body")}
                                </p>
                            )}
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelToteConfirm}
                                    disabled={confirmingTote}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className={`btn ${toteConfirm.sellable === "unsellable" ? "btn-danger" : "btn-success"} btn-lg`}
                                    onClick={confirmTotePutAway}
                                    disabled={confirmingTote}
                                >
                                    {confirmingTote ? t("submitting") : t("put_away_confirm_button")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {highMissingConfirm && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={cancelHighMissing}
                >
                    <div
                        className="modal-card book-in-close-modal"
                        data-testid="put-away-high-missing-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_high_missing_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelHighMissing}
                                disabled={confirmingHighMissing}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <AlertTriangle size={32} />
                                </div>
                                <div className="book-in-close-card-id">
                                    {highMissingConfirm.display}
                                </div>
                            </div>
                            <p className="book-in-close-hint book-in-close-hint--danger">
                                {t("put_away_high_missing_body")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelHighMissing}
                                    disabled={confirmingHighMissing}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={confirmHighMissing}
                                    disabled={confirmingHighMissing}
                                >
                                    {confirmingHighMissing ? t("submitting") : t("put_away_high_missing_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {coldMissingConfirm && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={cancelColdMissing}
                >
                    <div
                        className="modal-card book-in-close-modal"
                        data-testid="put-away-cold-missing-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_high_missing_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelColdMissing}
                                disabled={confirmingColdMissing}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <AlertTriangle size={32} />
                                </div>
                                <div className="book-in-close-card-id">
                                    {coldMissingConfirm.display}
                                </div>
                            </div>
                            <p className="book-in-close-hint book-in-close-hint--danger">
                                {t("put_away_high_missing_body")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelColdMissing}
                                    disabled={confirmingColdMissing}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={confirmColdMissing}
                                    disabled={confirmingColdMissing}
                                >
                                    {confirmingColdMissing ? t("submitting") : t("put_away_high_missing_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Found stock: a scanned product the count can't book on its
                own — book it into this location? Same decision, same
                wording family as Stock Check's own add prompt. */}

            {foundFinishConfirm && found && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={() => !foundBusy && setFoundFinishConfirmState(false)}
                >
                    <div
                        className="modal-card book-in-close-modal"
                        data-testid="put-away-found-finish-modal"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-card-header">
                            <h2>{t("put_away_found_finish_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setFoundFinishConfirmState(false)}
                                disabled={foundBusy}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <MapPin size={32} />
                                </div>
                                <div className="book-in-close-card-id">{found.location.display}</div>
                                <div className="book-in-close-card-divider" />
                                <div className="book-in-close-card-stats">
                                    <span className="book-in-close-card-count">{found.added}</span>
                                    <span className="book-in-close-card-count-label">
                                        {found.added === 1 ? t("item_singular") : t("item_plural")}
                                    </span>
                                </div>
                            </div>
                            <p className="book-in-close-hint">
                                {t("put_away_found_finish_body")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-lg"
                                    onClick={() => setFoundFinishConfirmState(false)}
                                    disabled={foundBusy}
                                >
                                    {t("picking_missing_confirm_keep_counting")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success btn-lg"
                                    onClick={() => finishFound()}
                                    disabled={foundBusy}
                                >
                                    {foundBusy ? t("submitting") : t("put_away_found_finish")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {unsellableConfirm && (
                <div
                    className="modal-overlay book-in-modal-overlay"
                    onClick={cancelUnsellable}
                >
                    <div className="modal-card book-in-close-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-card-header">
                            <h2>{t("unsellable_location_title")}</h2>
                            <button
                                className="modal-close"
                                onClick={cancelUnsellable}
                                disabled={confirmingUnsellable}
                                aria-label={t("cancel")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <div className="book-in-close-card">
                                <div className="book-in-close-card-icon">
                                    <AlertTriangle size={32} />
                                </div>
                                <div className="book-in-close-card-id">
                                    {unsellableConfirm.displayName || unsellableConfirm.locationId}
                                </div>
                            </div>
                            <p className="book-in-close-hint book-in-close-hint--danger">
                                {t("unsellable_location_body")}
                            </p>
                            <div className="modal-confirm-buttons">
                                <button
                                    className="btn btn-ghost btn-lg"
                                    onClick={cancelUnsellable}
                                    disabled={confirmingUnsellable}
                                >
                                    {t("cancel")}
                                </button>
                                <button
                                    className="btn btn-danger btn-lg"
                                    onClick={confirmUnsellable}
                                    disabled={confirmingUnsellable}
                                >
                                    {confirmingUnsellable ? t("submitting") : t("unsellable_location_confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Demo — toggled via Admin Settings → Demo Mode */}
            {demo && (
            <div className="book-in-demo-panel">
                <span className="book-in-demo-label">Demo</span>
                {phase === "awaiting-tote" && (
                    <>
                        <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE001")}>TOTE001</button>
                        <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE002")}>TOTE002</button>
                        <button className="book-in-demo-btn" onClick={() => doSubmit("TOTE003")}>TOTE003</button>
                    </>
                )}
                {phase === "awaiting-location" && (
                    <>
                        {remaining.slice(0, 5).map((barcode, i) => (
                            <button key={`${barcode}-${i}`} className="book-in-demo-btn" onClick={() => doSubmit(barcode)}>
                                {barcode}
                            </button>
                        ))}
                        {demoLocations.map(loc => (
                            <button key={loc} className="book-in-demo-btn" onClick={() => doSubmit(loc)}>{loc}</button>
                        ))}
                        {toteId && (
                            <button className="book-in-demo-btn book-in-demo-btn--tote" onClick={() => doSubmit(toteId)}>Close {toteId}</button>
                        )}
                    </>
                )}
                {phase === "awaiting-item-location" && (
                    <>
                        {demoLocations.map(loc => (
                            <button key={loc} className="book-in-demo-btn" onClick={() => doSubmit(loc)}>{loc}</button>
                        ))}
                        {toteId && (
                            <button className="book-in-demo-btn book-in-demo-btn--tote" onClick={() => doSubmit(toteId)}>Close {toteId}</button>
                        )}
                    </>
                )}
            </div>
            )}
        </div>
    );
};

export default PutAway;
